<template>
  <div class="conversation-workspace">
    <ConversationSidebar
      :items="sidebarItems"
      :active-tab="activeTab"
      :selected-id="selectedId"
      :loading="listLoading"
      :state-filter="stateFilter"
      :mentions-count="mentionsCount"
      :tab-unread="tabUnreadCounts"
      @update:activeTab="onTabChange"
      @update:stateFilter="onStateFilter"
      @search="onSearch"
      @select="selectConversation"
      @create="openNewChat"
      @open-mentions="openMentionsPanel"
    >
      <template #new-chat>
        <ConversationNewChat
          :open="newChatOpen"
          :mode="newChatMode"
          :busy="creatingChat"
          :recent-items="personalRecentItems"
          @close="newChatOpen = false"
          @create="createDirectChat"
          @select-existing="selectExistingFromNewChat"
        />
      </template>
    </ConversationSidebar>
    <ConversationChat
      ref="conversationChat"
      :conversation="selectedConversation"
      :messages="activeMessages"
      :members="activeMembers"
      :forward-targets="forwardTargets"
      :loading="messagesLoading"
      :sending="sending"
      :disabled="isSelectedClosed"
      :composer-hidden="isChannelSubscriberOnly"
      :state-busy="stateBusy"
      :members-count="activeMembers.length"
      :members-open="membersPanelOpen"
      :pending="isPendingSelection"
      :can-close-group="canCloseGroup"
      :can-delete-chat="canDeleteChat"
      :can-leave-group="canLeaveGroup"
      @send="handleSend"
      @edit-message="handleEditMessage"
      @delete-message="handleDeleteMessage"
      @forward-message="handleForwardMessage"
      @open-user-chat="openUserFromMention"
      @toggle-members="toggleMembersPanel"
      @close-chat="closeSelectedChat"
      @reopen="reopenSelectedChat"
      @delete-chat="deleteSelectedChat"
      @leave-group="leaveSelectedGroup"
    >
      <template #members>
        <ConversationMembers
          :open="membersPanelOpen"
          :members="activeMembers"
          :owner-id="selectedOwnerId"
          :loading="membersLoading"
          :busy="membersBusy"
          @close="membersPanelOpen = false"
          @add="addMember"
          @remove="removeMember"
        />
      </template>
    </ConversationChat>

    <div
      v-if="mentionsPanelOpen"
      class="conversation-mentions-overlay"
      @click.self="mentionsPanelOpen = false"
    >
      <aside class="conversation-mentions-panel" @click.stop>
        <header class="conversation-mentions-header">
          <div class="conversation-mentions-header-main">
            <v-icon size="18" color="#0066FF">mdi-at</v-icon>
            <span>{{ $t("conversation-my-mentions") }}</span>
            <span
              v-if="mentionsList.length && !mentionsLoading"
              class="conversation-mentions-count"
            >
              {{ toPersianDigits(mentionsList.length) }}
            </span>
          </div>
          <button
            type="button"
            class="conversation-mentions-close"
            @click="mentionsPanelOpen = false"
          >
            <v-icon size="18" color="#888">mdi-close</v-icon>
          </button>
        </header>
        <div class="conversation-mentions-body">
          <div v-if="mentionsLoading" class="conversation-mentions-loading">
            <v-progress-circular
              indeterminate
              color="#0066FF"
              size="24"
              width="2"
            />
          </div>
          <div
            v-else-if="!mentionsList.length"
            class="conversation-mentions-empty"
          >
            {{ $t("no-data-found") }}
          </div>
          <button
            v-for="item in mentionsList"
            :key="item.id"
            type="button"
            class="conversation-mentions-item"
            @click="openMentionItem(item)"
          >
            <div
              class="conversation-mentions-avatar"
              :style="{ background: mentionAvatarColor(item) }"
            >
              {{ item.initial }}
            </div>
            <div class="conversation-mentions-meta">
              <div class="conversation-mentions-row">
                <span class="conversation-mentions-item-title">
                  {{ item.chatTitle || ("#" + item.chatId) }}
                </span>
                <span class="conversation-mentions-item-time">{{
                  item.time
                }}</span>
              </div>
              <div class="conversation-mentions-item-text">
                <span
                  v-for="(part, idx) in mentionPreviewParts(
                    item.text,
                    item.mentionNames
                  )"
                  :key="'p-' + item.id + '-' + idx"
                  :class="{
                    'conversation-mentions-highlight': part.mention,
                  }"
                  >{{ part.text }}</span
                >
              </div>
            </div>
          </button>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import ConversationSidebar from "./ConversationSidebar.vue";
import ConversationChat from "./ConversationChat.vue";
import ConversationMembers from "./ConversationMembers.vue";
import ConversationNewChat from "./ConversationNewChat.vue";
import { normalizeChatEventName } from "@/realtime";

export default {
  name: "Conversation",
  components: {
    ConversationSidebar,
    ConversationChat,
    ConversationMembers,
    ConversationNewChat,
  },
  props: {
    refType: {
      type: String,
      default: null,
    },
    refId: {
      type: [Number, String],
      default: null,
    },
    title: {
      type: String,
      default: "",
    },
    ticketId: {
      type: [Number, String],
      default: null,
    },
    ticketTitle: {
      type: String,
      default: "",
    },
    focusChatId: {
      type: [Number, String],
      default: null,
    },
    focusMessageId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      conversations: [],
      messagesMap: {},
      membersMap: {},
      activeTab: "all",
      selectedId: null,
      listLoading: false,
      messagesLoading: false,
      sending: false,
      ensuringRef: false,
      bootstrapToken: 0,
      stateFilter: "",
      searchQuery: "",
      membersPanelOpen: false,
      membersLoading: false,
      membersBusy: false,
      stateBusy: false,
      newChatOpen: false,
      newChatMode: "personal",
      creatingChat: false,
      pendingConversation: null,
      myChatUserIdCache: null,
      totalUnread: 0,
      chatUnreadMeta: {},
      tabUnreadCounts: {
        personal: 0,
        group: 0,
        channel: 0,
        ticket: 0,
      },
      mentionsPanelOpen: false,
      mentionsLoading: false,
      mentionsList: [],
      mentionsCount: 0,
      subscribedChatRoomId: null,
      personalPeerCache: {},
      focusOpenTimer: null,
    };
  },
  created() {
    if (this.isSupport) {
      this.activeTab = "ticket";
    } else {
      this.activeTab = "all";
    }
    if (typeof sessionStorage === "undefined") {
      return;
    }
    try {
      var cached = sessionStorage.getItem("conversation-my-user-id");
      if (cached != null && cached !== "") {
        this.myChatUserIdCache = cached;
      }
    } catch (e) {}
    this.fetchMentionsCount();
  },
  mounted() {
    if (this.$root) {
      this.$root.$on("chat-live-event", this.onChatLiveEvent);
      this.$root.$on("chat-realtime-ready", this.onChatRealtimeReady);
    }
    this.syncChatRoomSubscription(this.selectedId);
    this.bootstrapTabUnreadCounts();
  },
  beforeDestroy() {
    if (this.focusOpenTimer) {
      clearTimeout(this.focusOpenTimer);
      this.focusOpenTimer = null;
    }
    if (this.$root) {
      this.$root.$off("chat-live-event", this.onChatLiveEvent);
      this.$root.$off("chat-realtime-ready", this.onChatRealtimeReady);
      this.$root.$emit("chat-active-changed", null);
    }
    this.syncChatRoomSubscription(null);
  },
  computed: {
    isSupport() {
      try {
        const token = JSON.parse(localStorage.getItem("token") || "{}");
        return (
          Number(
            token &&
              token.init &&
              token.init.appBarsOpacity &&
              token.init.appBarsOpacity.abr
          ) === 83
        );
      } catch (e) {
        return false;
      }
    },
    resolvedRefType() {
      if (this.refType) {
        return this.refType;
      }
      if (this.ticketId != null && this.ticketId !== "") {
        return "ticket";
      }
      return null;
    },
    resolvedRefId() {
      if (this.refId != null && this.refId !== "") {
        return this.refId;
      }
      return this.ticketId;
    },
    resolvedTitle() {
      return this.title || this.ticketTitle || "";
    },
    currentUserId() {
      return this.$store && this.$store.state ? this.$store.state.userId : null;
    },
    isPendingSelection() {
      return !!(
        this.pendingConversation &&
        this.selectedId != null &&
        String(this.selectedId) === String(this.pendingConversation.id)
      );
    },
    selectedConversation() {
      if (this.isPendingSelection) {
        return this.pendingConversation;
      }
      var id = this.selectedId;
      if (id == null) {
        return null;
      }
      return (
        this.conversations.find(function (item) {
          return Number(item.id) === Number(id);
        }) || null
      );
    },
    activeMessages() {
      if (this.selectedId == null || this.isPendingSelection) {
        return [];
      }
      return this.messagesMap[this.selectedId] || [];
    },
    activeMembers() {
      if (this.selectedId == null || this.isPendingSelection) {
        return [];
      }
      return this.membersMap[this.selectedId] || [];
    },
    selectedOwnerId() {
      var c = this.selectedConversation;
      return c && c.userId != null ? c.userId : null;
    },
    canCloseGroup() {
      var c = this.selectedConversation;
      if (!c || this.isPendingSelection) {
        return false;
      }
      if (c.type !== "group" && c.type !== "channel") {
        return false;
      }
      if (c.refType != null && c.refType !== "") {
        return false;
      }
      return this.isConversationOwner(c, this.activeMembers);
    },
    canDeleteChat() {
      var c = this.selectedConversation;
      if (!c || this.isPendingSelection) {
        return false;
      }
      // Personal: leave for me (remove own access)
      if (c.type === "personal") {
        return true;
      }
      // Group/channel: owner deletes the whole chat
      return this.canCloseGroup;
    },
    canLeaveGroup() {
      var c = this.selectedConversation;
      if (!c || this.isPendingSelection) {
        return false;
      }
      // Members only — not owner, and not channels
      if (c.type !== "group") {
        return false;
      }
      if (c.refType != null && c.refType !== "") {
        return false;
      }
      return !this.isConversationOwner(c, this.activeMembers);
    },
    forwardTargets() {
      var selected = this.selectedId;
      return (this.conversations || []).filter(function (item) {
        return (
          !item.isPending &&
          item.state !== "close" &&
          Number(item.id) !== Number(selected)
        );
      });
    },
    isSelectedClosed() {
      if (this.isPendingSelection) {
        return false;
      }
      var c = this.selectedConversation;
      return !!(c && c.state === "close");
    },
    isChannelSubscriberOnly() {
      if (this.isPendingSelection) {
        return false;
      }
      var c = this.selectedConversation;
      if (!c || c.type !== "channel") {
        return false;
      }
      return !this.isConversationOwner(c, this.activeMembers);
    },
    sidebarItems() {
      var list = this.conversations ? this.conversations.slice() : [];
      var pending = this.pendingConversation;
      if (!pending) {
        return list;
      }
      // Personal draft: show only while selected, never match by empty ref
      if (pending.type === "personal" && !pending.refType) {
        return [pending].concat(list);
      }
      var already = list.some(function (item) {
        return (
          String(item.refType || "") === String(pending.refType || "") &&
          Number(item.refId) === Number(pending.refId)
        );
      });
      if (already) {
        return list;
      }
      return [pending].concat(list);
    },
    personalRecentItems() {
      return (this.conversations || [])
        .filter(function (item) {
          return item && item.type === "personal" && !item.isPending;
        })
        .slice(0, 30);
    },
  },
  watch: {
    totalUnread() {
      // AppBar badge must stay global; refresh from unfiltered API.
      if (this.$root) {
        this.$root.$emit("chat-unread-refresh");
      }
    },
    resolvedRefId: {
      immediate: true,
      handler() {
        this.bootstrap();
      },
    },
    resolvedRefType() {
      this.bootstrap();
    },
    resolvedTitle() {
      this.syncFocusedTitle();
    },
    selectedId(next) {
      this.syncChatRoomSubscription(next);
      if (this.$root) {
        this.$root.$emit("chat-active-changed", next);
      }
    },
    focusChatId() {
      this.scheduleOpenFocusTarget();
    },
    focusMessageId() {
      this.scheduleOpenFocusTarget();
    },
  },
  methods: {
    bootstrap() {
      var self = this;
      var token = ++this.bootstrapToken;
      var refType = this.resolvedRefType;
      var refId = this.resolvedRefId;
      var hasFocus = refType != null && refId != null && refId !== "";

      // Keep chat panel in loading until list + by-ref resolve together
      if (hasFocus) {
        this.messagesLoading = true;
      }

      var listPromise = this.fetchConversations();
      var byRefPromise = hasFocus
        ? this.fetchChatByRef(refType, refId)
        : Promise.resolve(undefined);

      Promise.all([listPromise, byRefPromise])
        .then(function (results) {
          if (token !== self.bootstrapToken) {
            return;
          }
          return self.ensureFocusedConversation(results[1]);
        })
        .then(function () {
          if (token !== self.bootstrapToken) {
            return;
          }
          self.tryOpenFocusTarget();
        })
        .catch(function () {
          if (token === self.bootstrapToken && hasFocus) {
            self.messagesLoading = false;
          }
        });
    },
    tryOpenFocusTarget() {
      if (this.focusChatId == null || this.focusChatId === "") {
        return;
      }
      this.openMentionItem({
        chatId: this.focusChatId,
        id: this.focusMessageId,
      });
    },
    scheduleOpenFocusTarget() {
      var self = this;
      if (this.focusOpenTimer) {
        clearTimeout(this.focusOpenTimer);
      }
      this.focusOpenTimer = setTimeout(function () {
        self.focusOpenTimer = null;
        self.tryOpenFocusTarget();
      }, 0);
    },
    onTabChange(tab) {
      this.activeTab = tab;
      this.newChatOpen = false;
      this.clearPendingConversation();
      if (
        tab === "all" ||
        tab === "personal" ||
        tab === "group" ||
        tab === "channel"
      ) {
        this.selectedId = null;
        this.membersPanelOpen = false;
      }
      this.fetchConversations();
    },
    openNewChat() {
      if (this.activeTab === "all") {
        this.newChatMode = "compose";
      } else if (this.activeTab === "group") {
        this.newChatMode = "group";
      } else if (this.activeTab === "channel") {
        this.newChatMode = "channel";
      } else {
        this.newChatMode = "personal";
      }
      this.clearPendingConversation();
      this.newChatOpen = true;
    },
    selectExistingFromNewChat(item) {
      if (!item) {
        return;
      }
      this.newChatOpen = false;
      this.selectConversation(item);
    },
    onSearch(value) {
      this.searchQuery = value || "";
      this.fetchConversations();
    },
    onStateFilter(value) {
      this.stateFilter = value || "";
      this.fetchConversations();
    },
    fetchConversations(options) {
      var self = this;
      var opts = options || {};
      var preferredSelectedId =
        opts.preferredSelectedId != null ? opts.preferredSelectedId : null;
      this.listLoading = true;
      var params = {
        options: JSON.stringify({
          page: 1,
          itemsPerPage: 50,
          sortDesc: [true],
        }),
        sort: "newest_message",
      };
      if (this.searchQuery) {
        params.search = this.searchQuery;
      }
      if (this.stateFilter) {
        params.state = this.stateFilter;
      }
      if (
        this.activeTab === "personal" ||
        this.activeTab === "group" ||
        this.activeTab === "channel"
      ) {
        params.type = this.toApiChatType(this.activeTab) || this.activeTab;
      }

      return this.$axios
        .get(this.$endpoint + "chat", { params: params })
        .then(function (response) {
          var body = response && response.data ? response.data : {};
          // total_unread in typed list responses can be scoped to that type.
          // Keep local/global total only from unfiltered list responses.
          if (body.total_unread != null && !params.type) {
            self.totalUnread = Number(body.total_unread) || 0;
          }
          var rows = self.extractList(response);
          var previousSelected =
            preferredSelectedId != null
              ? preferredSelectedId
              : self.selectedId;
          var previousTypes = {};
          var previousPeers = {};
          self.conversations.forEach(function (item) {
            previousTypes[item.id] = item.type;
            if (item.peerUserId != null) {
              previousPeers[item.id] = item.peerUserId;
            }
          });
          // Also keep peer from cache when remapping personal chats
          Object.keys(self.personalPeerCache || {}).forEach(function (peerKey) {
            var cached = self.personalPeerCache[peerKey];
            if (cached && cached.id != null && cached.peerUserId != null) {
              previousPeers[cached.id] = cached.peerUserId;
              previousTypes[cached.id] = "personal";
            }
          });
          self.conversations = rows
            .map(function (row) {
              var remembered = self.getRememberedGroup(row.id);
              var forced = null;
              if (row.type === "channel" || previousTypes[row.id] === "channel") {
                forced = "channel";
              } else if (
                previousTypes[row.id] === "personal" ||
                previousTypes[row.id] === "group"
              ) {
                forced = previousTypes[row.id];
              } else if (remembered && remembered.type === "channel") {
                forced = "channel";
              } else if (remembered) {
                forced = "group";
              }
              var knownMembers = self.membersMap[row.id];
              var memberCount = knownMembers ? knownMembers.length : null;
              var mapped = self.mapChatToConversation(row, forced, memberCount);
              if (remembered && remembered.title && !(row.name || "").trim()) {
                mapped.title = remembered.title;
              }
              if (mapped.peerUserId == null && previousPeers[row.id] != null) {
                mapped.peerUserId = previousPeers[row.id];
              }
              if (
                mapped.peerUserId == null &&
                mapped.type === "personal" &&
                Array.isArray(knownMembers) &&
                knownMembers.length
              ) {
                mapped.peerUserId = self.resolvePersonalPeerId(
                  row,
                  knownMembers
                );
              }
              self.rememberPersonalPeer(mapped);
              self.maybeRememberMyChatUserIdFromConversation(mapped);
              return mapped;
            })
            .filter(function (mapped) {
              if (self.activeTab !== "all") {
                return true;
              }
              return (
                mapped.type === "personal" ||
                mapped.type === "group" ||
                mapped.type === "channel"
              );
            });
          self.syncChatUnreadMetaFromList(
            self.conversations,
            self.activeTab === "all"
              ? ["personal", "group", "channel"]
              : self.activeTab
          );
          if (
            previousSelected != null &&
            !self.conversations.some(function (item) {
              return Number(item.id) === Number(previousSelected);
            })
          ) {
            if (
              preferredSelectedId != null &&
              Number(preferredSelectedId) === Number(previousSelected)
            ) {
              // Keep preferred id; caller will inject/select if missing from list
              self.selectedId = preferredSelectedId;
            } else if (
              !(
                self.pendingConversation &&
                String(previousSelected) ===
                  String(self.pendingConversation.id)
              )
            ) {
              self.selectedId = null;
              self.membersPanelOpen = false;
            }
          } else if (preferredSelectedId != null) {
            self.selectedId = preferredSelectedId;
          }
        })
        .catch(function (error) {
          console.error("chat list error:", error);
          self.conversations = [];
        })
        .finally(function () {
          self.listLoading = false;
        });
    },
    ensureFocusedConversation(prefetchedChat) {
      var self = this;
      var refType = this.resolvedRefType;
      var refId = this.resolvedRefId;

      if (refType == null || refId == null || refId === "") {
        this.clearPendingConversation();
        this.selectedId = null;
        this.membersPanelOpen = false;
        this.messagesLoading = false;
        return Promise.resolve();
      }

      this.activeTab = refType === "ticket" ? "ticket" : this.activeTab;

      var applyFocusedChat = function (chat) {
        if (chat) {
          self.clearPendingConversation();
          var mapped = self.upsertConversation(chat);
          self.syncFocusedTitle();
          return self.selectConversation(mapped);
        }
        // No server chat yet — keep local empty draft; create only on first message
        self.selectPendingDraft(refType, refId);
        self.messagesLoading = false;
        return Promise.resolve();
      };

      var matched = this.findByRef(refType, refId, this.conversations);
      if (matched) {
        this.clearPendingConversation();
        this.syncFocusedTitle();
        return this.selectConversation(matched);
      }

      if (prefetchedChat !== undefined) {
        return applyFocusedChat(prefetchedChat);
      }

      if (this.ensuringRef) {
        return Promise.resolve();
      }

      this.ensuringRef = true;
      this.messagesLoading = true;
      return this.fetchChatByRef(refType, refId)
        .then(applyFocusedChat)
        .finally(function () {
          self.ensuringRef = false;
        });
    },
    selectPendingDraft(refType, refId) {
      var pendingId = "pending-" + refType + "-" + refId;
      this.pendingConversation = {
        id: pendingId,
        type: refType === "ticket" ? "ticket" : "group",
        refType: refType,
        refId: refId,
        ticketId: refType === "ticket" ? Number(refId) : null,
        title: this.resolvedTitle || "",
        preview: "",
        time: "",
        unread: 0,
        avatarColor: this.avatarColorForId(refId),
        state: "open",
        priority: "normal",
        user: null,
        userId: this.currentUserId,
        members: 0,
        isPending: true,
      };
      this.selectedId = pendingId;
      this.membersPanelOpen = false;
      this.activeTab = refType === "ticket" ? "ticket" : this.activeTab;
    },
    clearPendingConversation() {
      var pending = this.pendingConversation;
      if (
        pending &&
        this.selectedId != null &&
        String(this.selectedId) === String(pending.id)
      ) {
        this.selectedId = null;
      }
      if (pending && pending.id != null && this.messagesMap[pending.id]) {
        this.$delete(this.messagesMap, pending.id);
      }
      this.pendingConversation = null;
    },
    openPersonalPending(payload) {
      var userIds = (payload && payload.userIds) || [];
      var user =
        payload && payload.users && payload.users[0]
          ? payload.users[0]
          : userIds[0] != null
            ? { id: userIds[0] }
            : null;
      if (!user || user.id == null) {
        return;
      }

      var self = this;
      this.resolvePersonalConversationByPeer(user.id).then(function (existing) {
        if (existing) {
          self.openExistingPersonalChat(existing);
          return;
        }
        self.createLocalPersonalPending(user);
      });
    },
    createLocalPersonalPending(user) {
      if (!user || user.id == null) {
        return;
      }
      var pendingId = "pending-personal-" + user.id;
      this.searchQuery = "";
      if (
        this.pendingConversation &&
        String(this.pendingConversation.id) === String(pendingId)
      ) {
        this.selectedId = pendingId;
        if (this.activeTab !== "all") {
          this.activeTab = "personal";
        }
        this.newChatOpen = false;
        return;
      }
      this.pendingConversation = {
        id: pendingId,
        type: "personal",
        refType: null,
        refId: null,
        peerUserId: user.id,
        peerUser: user,
        userIds: [user.id],
        title: this.formatUserName(user) || "#" + user.id,
        preview: "",
        time: "",
        unread: 0,
        avatarUrl: this.resolveUserAvatarUrl(user),
        avatarColor: this.avatarColorForId(user.id),
        state: "open",
        priority: "normal",
        user: user,
        userId: this.resolveMyChatUserId() || this.currentUserId,
        members: 2,
        isPending: true,
      };
      this.selectedId = pendingId;
      if (this.activeTab !== "all") {
        this.activeTab = "personal";
      }
      this.newChatOpen = false;
      this.membersPanelOpen = false;
      this.messagesLoading = false;
      this.$set(this.messagesMap, pendingId, []);
      // Refresh personal list in background so sidebar stays consistent
      this.fetchConversations({ preferredSelectedId: pendingId });
    },
    rememberPersonalPeer(conversation) {
      if (
        !conversation ||
        conversation.type !== "personal" ||
        conversation.peerUserId == null ||
        conversation.isPending
      ) {
        return;
      }
      this.$set(this.personalPeerCache, String(conversation.peerUserId), {
        id: conversation.id,
        type: "personal",
        peerUserId: conversation.peerUserId,
        title: conversation.title,
        preview: conversation.preview || "",
        time: conversation.time || "",
        unread: conversation.unread || 0,
        avatarColor:
          conversation.avatarColor ||
          this.avatarColorForId(conversation.peerUserId),
        state: conversation.state || "open",
        priority: conversation.priority || "normal",
        userId: conversation.userId,
        members: conversation.members || 2,
      });
    },
    openExistingPersonalChat(conversation) {
      if (!conversation || conversation.id == null) {
        return Promise.resolve();
      }
      var self = this;
      var peerId = conversation.peerUserId;
      var targetId = conversation.id;

      this.clearPendingConversation();
      if (this.activeTab !== "all") {
        this.activeTab = "personal";
      }
      this.newChatOpen = false;
      this.membersPanelOpen = false;
      // Clear list search so the target personal chat is not filtered out
      this.searchQuery = "";
      this.selectedId = targetId;
      this.rememberPersonalPeer(conversation);

      return this.fetchConversations({
        preferredSelectedId: targetId,
      })
        .then(function () {
          var found =
            (peerId != null && self.findPersonalConversationByPeer(peerId)) ||
            self.conversations.find(function (item) {
              return Number(item.id) === Number(targetId);
            });
          if (!found) {
            var injected = Object.assign({}, conversation, {
              type: "personal",
              peerUserId:
                peerId != null ? peerId : conversation.peerUserId,
            });
            self.conversations.unshift(injected);
            found = injected;
          }
          if (found.peerUserId == null && peerId != null) {
            self.$set(found, "peerUserId", peerId);
          }
          self.rememberPersonalPeer(found);
          return self.selectConversation(found);
        })
        .catch(function (error) {
          console.error("open existing personal chat error:", error);
          // Fallback: still try to open the known conversation object
          if (self.activeTab !== "all") {
            self.activeTab = "personal";
          }
          if (
            !self.conversations.some(function (item) {
              return Number(item.id) === Number(targetId);
            })
          ) {
            self.conversations.unshift(conversation);
          }
          return self.selectConversation(conversation);
        });
    },
    findPartnerUser(userId, name) {
      var self = this;
      if (userId == null) {
        return Promise.resolve(null);
      }
      var matchInList = function (list) {
        if (!Array.isArray(list)) {
          return null;
        }
        for (var i = 0; i < list.length; i++) {
          if (list[i] && Number(list[i].id) === Number(userId)) {
            return list[i];
          }
        }
        return null;
      };
      var fetchPartners = function (search) {
        var params = {
          select: "partners",
          page: 1,
        };
        if (search) {
          params.search = search;
        }
        return self.$axios
          .get(self.$endpoint + "getdetail", { params: params })
          .then(function (response) {
            var body = response && response.data ? response.data : {};
            return matchInList(Array.isArray(body.data) ? body.data : []);
          });
      };

      var queries = [];
      var trimmed = this.sanitizeUserSearchName(name);
      if (trimmed) {
        queries.push(trimmed);
        var first = trimmed.split(/\s+/).filter(Boolean)[0];
        if (first && first !== trimmed) {
          queries.push(first);
        }
      }
      // Always end with unfiltered partners page (match by id)
      queries.push("");

      var run = function (index) {
        if (index >= queries.length) {
          return Promise.resolve(null);
        }
        return fetchPartners(queries[index]).then(function (found) {
          if (found) {
            return found;
          }
          return run(index + 1);
        });
      };

      return run(0).catch(function (error) {
        console.error("find partner user error:", error);
        return null;
      });
    },
    sanitizeUserSearchName(name) {
      var trimmed = String(name || "").trim();
      if (!trimmed) {
        return "";
      }
      // Ticket titles / ids often leak as "#7532" — never use those for partner search
      if (trimmed.charAt(0) === "#") {
        return "";
      }
      if (/^\d+$/.test(trimmed)) {
        return "";
      }
      if (/^#?\d+(\s|$)/.test(trimmed)) {
        return "";
      }
      return trimmed;
    },
    buildOpenChatUser(found, userId, name) {
      var safeName = this.sanitizeUserSearchName(name) || String(name || "").trim();
      if (safeName.charAt(0) === "#") {
        safeName = "";
      }
      if (found) {
        return {
          id: found.id,
          firstname: found.firstname,
          lastname: found.lastname,
          name:
            found.name ||
            [found.firstname, found.lastname].filter(Boolean).join(" ").trim() ||
            safeName ||
            String(found.id),
          uid: found.uid,
          avatar: found.avatar,
        };
      }
      return {
        id: userId,
        name: safeName || String(userId),
      };
    },
    resolvePersonalConversationByPeer(peerUserId) {
      if (peerUserId == null) {
        return Promise.resolve(null);
      }
      var local = this.findPersonalConversationByPeer(peerUserId);
      if (local) {
        return Promise.resolve(local);
      }
      var cached = this.personalPeerCache[String(peerUserId)];
      if (cached) {
        return Promise.resolve(cached);
      }

      var self = this;
      return this.$axios
        .get(this.$endpoint + "chat", {
          params: {
            type: "direct",
            sort: "newest_message",
            options: JSON.stringify({
              page: 1,
              itemsPerPage: 50,
              sortDesc: [true],
            }),
          },
        })
        .then(function (response) {
          var rows = self.extractList(response) || [];
          var missingPeerRows = [];
          for (var i = 0; i < rows.length; i++) {
            var mapped = self.mapChatToConversation(rows[i], "personal");
            if (mapped.peerUserId == null) {
              missingPeerRows.push({ row: rows[i], mapped: mapped });
            } else {
              self.rememberPersonalPeer(mapped);
              if (Number(mapped.peerUserId) === Number(peerUserId)) {
                return mapped;
              }
            }
          }
          if (!missingPeerRows.length) {
            return null;
          }
          return self.hydratePersonalPeerFromMembers(
            missingPeerRows,
            peerUserId
          );
        })
        .catch(function (error) {
          console.error("resolve personal chat by peer error:", error);
          return null;
        });
    },
    hydratePersonalPeerFromMembers(entries, targetPeerId) {
      var self = this;
      var queue = (entries || []).slice();
      var next = function () {
        if (!queue.length) {
          return Promise.resolve(null);
        }
        var entry = queue.shift();
        var chatId = entry.row && entry.row.id;
        if (chatId == null) {
          return next();
        }
        return self.$axios
          .get(self.$endpoint + "chatUserAccess", {
            params: { chat_id: chatId },
          })
          .then(function (response) {
            var members = self.normalizeAccessRows(
              self.extractAccessList(response)
            );
            self.$set(self.membersMap, chatId, members);
            var peerId = self.resolvePersonalPeerId(entry.row, members);
            var mapped = Object.assign({}, entry.mapped, {
              peerUserId: peerId,
            });
            if (peerId != null) {
              self.rememberPersonalPeer(mapped);
            }
            if (peerId != null && Number(peerId) === Number(targetPeerId)) {
              return mapped;
            }
            return next();
          })
          .catch(function () {
            return next();
          });
      };
      return next();
    },
    findPersonalConversationByPeer(peerUserId) {
      if (peerUserId == null) {
        return null;
      }
      var peer = Number(peerUserId);
      var pending = this.pendingConversation;
      if (
        pending &&
        pending.type === "personal" &&
        pending.peerUserId != null &&
        Number(pending.peerUserId) === peer
      ) {
        return pending;
      }

      var list = this.conversations || [];
      for (var i = 0; i < list.length; i++) {
        var item = list[i];
        if (!item || item.type !== "personal") {
          continue;
        }
        if (item.peerUserId != null && Number(item.peerUserId) === peer) {
          return item;
        }
        var members = this.membersMap && this.membersMap[item.id];
        if (Array.isArray(members) && members.length) {
          for (var m = 0; m < members.length; m++) {
            var uid = this.resolveMemberUserId(members[m]);
            if (
              uid != null &&
              Number(uid) === peer &&
              !this.isAccessCurrentUser(members[m])
            ) {
              if (item.peerUserId == null) {
                this.$set(item, "peerUserId", uid);
              }
              this.rememberPersonalPeer(item);
              return item;
            }
          }
        }
      }

      var cached = this.personalPeerCache[String(peerUserId)];
      if (cached) {
        return cached;
      }
      return null;
    },
    fetchChatByRef(refType, refId) {
      var self = this;
      return this.$axios
        .get(this.$endpoint + "chat", {
          params: {
            ref_type: refType,
            ref_id: refId,
            options: JSON.stringify({
              page: 1,
              itemsPerPage: 5,
              sortDesc: [true],
            }),
          },
        })
        .then(function (response) {
          var rows = self.extractList(response);
          return rows && rows.length ? rows[0] : null;
        })
        .catch(function (error) {
          console.error("chat by ref error:", error);
          return null;
        });
    },
    createChatForRef(refType, refId, message) {
      var payload = {
        ref_type: refType,
        ref_id: refId,
        // Ticket type is inferred by backend from ref_*; do not send "ticket"/"group"
        type: refType === "ticket" ? null : "group",
        state: "open",
        priority: "normal",
      };
      if (message) {
        payload.message = message;
      }
      return this.$axios
        .post(this.$endpoint + "chat", payload)
        .then(function (response) {
          var body = response && response.data ? response.data : {};
          var data = body.data || {};
          return {
            chat: data.chat || data || null,
            firstMessage: data.first_message || null,
          };
        })
        .catch(function (error) {
          console.error("create chat error:", error);
          return { chat: null, firstMessage: null };
        });
    },
    createDirectChat(payload) {
      var self = this;
      if (!payload || this.creatingChat) {
        return;
      }
      var mode =
        payload.mode === "group"
          ? "group"
          : payload.mode === "channel"
            ? "channel"
            : "personal";
      var apiType = this.toApiChatType(mode) || "direct";
      var userIds = payload.userIds || [];
      if (userIds.length < 1) {
        return;
      }
      if (
        (mode === "group" || mode === "channel") &&
        !(payload.groupName || "").trim()
      ) {
        return;
      }

      // Personal without first message → local draft only (Telegram-like)
      if (mode === "personal" && !(payload.message || "").trim()) {
        this.openPersonalPending(payload);
        return;
      }

      this.creatingChat = true;
      var body = {
        user_ids: userIds,
        type: apiType,
        state: "open",
        priority: "normal",
      };
      if (payload.message) {
        body.message = payload.message;
      }
      if (mode === "group" || mode === "channel") {
        body.name = (payload.groupName || "").trim();
      }

      this.$axios
        .post(this.$endpoint + "chat", body)
        .then(function (response) {
          var resBody = response && response.data ? response.data : {};
          var data = resBody.data || {};
          var chat = data.chat || data || null;
          if (!chat) {
            return;
          }
          if (!chat.type) {
            chat.type = apiType;
          }
          var mapped = self.mapChatToConversation(
            chat,
            mode,
            userIds.length + 1
          );
          var ownerId =
            chat.user_id != null
              ? chat.user_id
              : chat.user && chat.user.id != null
                ? chat.user.id
                : data.first_message && data.first_message.user_id != null
                  ? data.first_message.user_id
                  : null;
          if (ownerId != null) {
            mapped.userId = ownerId;
            self.rememberMyChatUserId(ownerId);
          }
          if (mode === "personal" && payload.users && payload.users[0]) {
            mapped.title = self.formatUserName(payload.users[0]);
            mapped.peerUserId = payload.users[0].id;
          } else if (mode === "group" || mode === "channel") {
            mapped.title = (payload.groupName || "").trim();
            mapped.type = mode;
            mapped.members = userIds.length + 1;
            self.rememberGroupChat(mapped.id, mapped.title, ownerId, mode);
          }
          if (payload.message) {
            mapped.preview = payload.message;
          }
          self.upsertMappedConversation(mapped);
          if (self.activeTab !== "all") {
            self.activeTab = mode;
          }
          self.newChatOpen = false;
          self.pendingConversation = null;
          return self.selectConversation(mapped);
        })
        .catch(function (error) {
          console.error("create direct chat error:", error);
        })
        .finally(function () {
          self.creatingChat = false;
        });
    },
    buildGroupTitle(users) {
      var self = this;
      var names = (users || [])
        .slice(0, 3)
        .map(function (u) {
          return self.formatUserName(u);
        })
        .filter(Boolean);
      if (!names.length) {
        return this.$t("group");
      }
      if (users.length > 3) {
        return names.join("، ") + "…";
      }
      return names.join("، ");
    },
    selectConversation(item) {
      if (!item) {
        return Promise.resolve();
      }
      if (item.isPending) {
        this.pendingConversation = item;
        this.selectedId = item.id;
        if (this.activeTab !== "all") {
          this.activeTab = item.type || this.activeTab;
        }
        this.membersPanelOpen = false;
        this.messagesLoading = false;
        return Promise.resolve();
      }
      this.clearPendingConversation();
      var changed = Number(this.selectedId) !== Number(item.id);
      this.selectedId = item.id;
      if (this.activeTab !== "all") {
        this.activeTab = item.type || this.activeTab;
      }
      if (changed) {
        this.membersPanelOpen = false;
      }
      var self = this;
      // Learn numeric chat user id from members before mapping messages
      return this.loadMembers(item.id, true)
        .then(function () {
          return self.loadMessages(item.id, true);
        })
        .then(function () {
          self.reclassifyMessagesMine(item.id);
          self.enrichMessagesSenders(item.id);
          return self.markChatAsRead(item.id);
        });
    },
    markChatAsRead(chatId) {
      var self = this;
      if (chatId == null || this.isPendingSelection) {
        return Promise.resolve();
      }
      var conversation = this.conversations.find(function (item) {
        return Number(item.id) === Number(chatId);
      });
      var hadUnread = conversation && Number(conversation.unread) > 0;
      if (!hadUnread && conversation) {
        return Promise.resolve();
      }

      return this.$axios
        .post(this.$endpoint + "chatMessageRead", {
          chat_id: chatId,
          mark_all: true,
        })
        .then(function (response) {
          var body = response && response.data ? response.data : {};
          var data = body.data || {};
          var nextUnread =
            data.unread_count != null ? Number(data.unread_count) || 0 : 0;
          if (conversation) {
            var prev = Number(conversation.unread) || 0;
            self.$set(conversation, "unread", nextUnread);
            if (prev > nextUnread) {
              self.totalUnread = Math.max(
                0,
                (Number(self.totalUnread) || 0) - (prev - nextUnread)
              );
            }
            self.setChatUnreadMeta(
              chatId,
              nextUnread,
              conversation.type || "personal"
            );
          } else {
            var meta = self.chatUnreadMeta[String(chatId)];
            if (meta) {
              self.setChatUnreadMeta(chatId, nextUnread, meta.type);
            }
          }
        })
        .catch(function (error) {
          console.error("mark chat read error:", error);
        });
    },
    loadMessages(chatId, force) {
      var self = this;
      if (chatId == null) {
        return Promise.resolve();
      }
      if (!force && this.messagesMap[chatId]) {
        return Promise.resolve();
      }

      this.messagesLoading = true;
      return this.$axios
        .get(this.$endpoint + "chatMessage", {
          params: {
            chat_id: chatId,
            options: JSON.stringify({
              page: 1,
              itemsPerPage: 100,
              sortDesc: [false],
            }),
          },
        })
        .then(function (response) {
          var body = response && response.data ? response.data : {};
          var rows = self.extractMessageList(response);
          // Learn numeric id from message authors when uid matches store userId
          rows.forEach(function (row) {
            if (
              row &&
              row.user &&
              row.user.uid != null &&
              self.isSameUserId(row.user.uid, self.currentUserId)
            ) {
              var id =
                row.user_id != null
                  ? row.user_id
                  : row.user.id != null
                    ? row.user.id
                    : null;
              if (id != null) {
                self.rememberMyChatUserId(id);
              }
            }
          });
          var mapped = rows.map(function (row) {
            return self.mapMessage(row, chatId);
          });
          self.$set(self.messagesMap, chatId, mapped);
          self.enrichMessagesSenders(chatId);
          if (body.unread_count != null) {
            var nextUnread = Number(body.unread_count) || 0;
            var conversation = self.conversations.find(function (item) {
              return Number(item.id) === Number(chatId);
            });
            if (conversation) {
              self.$set(conversation, "unread", nextUnread);
              self.setChatUnreadMeta(
                chatId,
                nextUnread,
                conversation.type || "personal"
              );
            } else {
              var meta = self.chatUnreadMeta[String(chatId)];
              if (meta) {
                self.setChatUnreadMeta(chatId, nextUnread, meta.type);
              }
            }
          }
        })
        .catch(function (error) {
          console.error("chat messages error:", error);
          self.$set(self.messagesMap, chatId, []);
        })
        .finally(function () {
          self.messagesLoading = false;
        });
    },
    loadMembers(chatId, force) {
      var self = this;
      if (chatId == null) {
        return Promise.resolve();
      }
      if (!force && this.membersMap[chatId]) {
        return Promise.resolve();
      }

      this.membersLoading = true;
      return this.$axios
        .get(this.$endpoint + "chatUserAccess", {
          params: { chat_id: chatId },
        })
        .then(function (response) {
          var rows = self.normalizeAccessRows(self.extractAccessList(response));
          self.$set(self.membersMap, chatId, rows);
          self.learnIdentityFromMembers(rows);
          self.learnMyIdFromPersonalMembers(chatId, rows);
          self.reclassifyConversation(chatId, rows);
          self.reclassifyMessagesMine(chatId);
          self.enrichMessagesSenders(chatId);
          var conversation = self.conversations.find(function (item) {
            return Number(item.id) === Number(chatId);
          });
          if (
            conversation &&
            conversation.type === "personal" &&
            conversation.peerUserId == null
          ) {
            var peerId = self.resolvePersonalPeerId(null, rows);
            if (peerId != null) {
              self.$set(conversation, "peerUserId", peerId);
              self.rememberPersonalPeer(conversation);
            }
          } else if (
            conversation &&
            conversation.type === "personal" &&
            conversation.peerUserId != null
          ) {
            self.rememberPersonalPeer(conversation);
          }
        })
        .catch(function (error) {
          console.error("chat members error:", error);
          self.$set(self.membersMap, chatId, []);
        })
        .finally(function () {
          self.membersLoading = false;
        });
    },
    reclassifyConversation(chatId, members) {
      var conversation = this.conversations.find(function (item) {
        return Number(item.id) === Number(chatId);
      });
      if (!conversation) {
        return;
      }
      conversation.members = (members || []).length;
      if (conversation.refType === "ticket") {
        conversation.type = "ticket";
        return;
      }
      if (conversation.type === "channel") {
        return;
      }
      if (conversation.refType) {
        conversation.type = "group";
        return;
      }
      var remembered = this.getRememberedGroup(chatId);
      if (remembered && remembered.type === "channel") {
        conversation.type = "channel";
        if (remembered.title && !(conversation.title || "").trim()) {
          conversation.title = remembered.title;
        }
        return;
      }
      // Never demote an explicit group chat to personal
      if (conversation.type === "group" || remembered) {
        conversation.type = "group";
        if (remembered && remembered.title) {
          conversation.title = remembered.title;
        }
        return;
      }
      var nextType = this.resolveChatType(
        { ref_type: conversation.refType, access_users: members },
        members.length
      );
      // Only allow upgrade personal -> group by member count
      if (nextType === "group") {
        conversation.type = "group";
        return;
      }
      conversation.type = "personal";
      if (
        !conversation.title ||
        String(conversation.title).indexOf("#") === 0
      ) {
        var other = this.findOtherMember(members);
        if (other) {
          conversation.title =
            this.formatUserName(other.user || other) || conversation.title;
        }
      }
    },
    rememberGroupChat(chatId, title, ownerId, type) {
      if (chatId == null || typeof sessionStorage === "undefined") {
        return;
      }
      try {
        var key = "conversation-group-meta";
        var raw = sessionStorage.getItem(key);
        var map = raw ? JSON.parse(raw) : {};
        if (!map || typeof map !== "object") {
          map = {};
        }
        map[String(chatId)] = {
          title: title || "",
          ownerId: ownerId != null ? ownerId : null,
          createdByMe: true,
          type: type === "channel" ? "channel" : "group",
        };
        sessionStorage.setItem(key, JSON.stringify(map));
      } catch (e) {}
    },
    getRememberedGroup(chatId) {
      if (chatId == null || typeof sessionStorage === "undefined") {
        return null;
      }
      try {
        var raw = sessionStorage.getItem("conversation-group-meta");
        if (!raw) {
          return null;
        }
        var map = JSON.parse(raw);
        return map && map[String(chatId)] ? map[String(chatId)] : null;
      } catch (e) {
        return null;
      }
    },
    rememberMyChatUserId(userId) {
      if (userId == null) {
        return;
      }
      this.myChatUserIdCache = userId;
      if (typeof sessionStorage === "undefined") {
        return;
      }
      try {
        sessionStorage.setItem("conversation-my-user-id", String(userId));
      } catch (e) {}
    },
    resolveMyChatUserId() {
      if (this.myChatUserIdCache != null && this.myChatUserIdCache !== "") {
        return this.myChatUserIdCache;
      }
      if (typeof sessionStorage !== "undefined") {
        try {
          var cached = sessionStorage.getItem("conversation-my-user-id");
          if (cached != null && cached !== "") {
            this.myChatUserIdCache = cached;
            return cached;
          }
        } catch (e) {}
      }
      return this.currentUserId;
    },
    isSameUserId(a, b) {
      if (a == null || b == null) {
        return false;
      }
      if (String(a) === String(b)) {
        return true;
      }
      var na = Number(a);
      var nb = Number(b);
      return !isNaN(na) && !isNaN(nb) && na === nb;
    },
    isCurrentUserIdentity(value) {
      if (value == null) {
        return false;
      }
      if (this.isSameUserId(value, this.currentUserId)) {
        return true;
      }
      if (this.isSameUserId(value, this.myChatUserIdCache)) {
        return true;
      }
      if (this.isSameUserId(value, this.resolveMyChatUserId())) {
        return true;
      }
      return false;
    },
    isNumericChatUserId(value) {
      if (value == null || value === "") {
        return false;
      }
      var n = Number(value);
      return !isNaN(n) && String(n) === String(value).trim();
    },
    learnMyIdFromPersonalMembers(chatId, members) {
      var conversation = this.findConversationById(chatId);
      if (
        !conversation ||
        conversation.type !== "personal" ||
        conversation.peerUserId == null
      ) {
        return;
      }
      var list = members || [];
      for (var i = 0; i < list.length; i++) {
        var uid = this.resolveMemberUserId(list[i]);
        if (
          uid != null &&
          !this.isSameUserId(uid, conversation.peerUserId)
        ) {
          this.rememberMyChatUserId(uid);
          return;
        }
      }
    },
    findMyLiveBadge(data) {
      if (!data || !Array.isArray(data.badges)) {
        return null;
      }
      var badges = data.badges;
      for (var i = 0; i < badges.length; i++) {
        var badge = badges[i];
        if (!badge) {
          continue;
        }
        var user = badge.user || null;
        var candidates = [
          badge.user_id,
          user && user.id,
          user && user.user_id,
          user && user.uid,
        ];
        for (var c = 0; c < candidates.length; c++) {
          if (this.isCurrentUserIdentity(candidates[c])) {
            if (badge.user_id != null) {
              this.rememberMyChatUserId(badge.user_id);
            } else if (user && user.id != null) {
              this.rememberMyChatUserId(user.id);
            }
            return badge;
          }
        }
      }

      // DM fallback: token userId may be uid-string while badges use numeric ids.
      // If this chat is already in my list and there are exactly 2 badges, pick
      // the non-sender badge when we cannot identify ourselves as the sender.
      var chatId = this.liveChatId(data);
      var senderId =
        data.sender_id != null
          ? data.sender_id
          : data.message && data.message.user_id != null
            ? data.message.user_id
            : null;
      if (
        chatId != null &&
        this.findConversationById(chatId) &&
        badges.length === 2 &&
        senderId != null &&
        !this.isCurrentUserIdentity(senderId) &&
        !this.isNumericChatUserId(this.resolveMyChatUserId())
      ) {
        for (var j = 0; j < badges.length; j++) {
          var other = badges[j];
          if (!other) {
            continue;
          }
          var otherId =
            other.user_id != null
              ? other.user_id
              : other.user && other.user.id;
          if (otherId != null && !this.isSameUserId(otherId, senderId)) {
            this.rememberMyChatUserId(otherId);
            return other;
          }
        }
      }
      return null;
    },
    isAccessCurrentUser(access) {
      if (!access) {
        return false;
      }
      var user = this.resolveMemberUser(access);
      var candidates = [
        this.resolveMemberUserId(access),
        access.user_id,
        access.uid,
        user && user.id,
        user && user.user_id,
        user && user.uid,
      ];
      for (var i = 0; i < candidates.length; i++) {
        if (this.isCurrentUserIdentity(candidates[i])) {
          return true;
        }
      }
      return false;
    },
    findOwnerAccess(members, conversation) {
      var list = members || [];
      if (conversation && conversation.userId != null) {
        for (var i = 0; i < list.length; i++) {
          var uid = this.resolveMemberUserId(list[i]);
          if (this.isSameUserId(uid, conversation.userId)) {
            return list[i];
          }
        }
      }
      return null;
    },
    learnIdentityFromMembers(members) {
      var list = members || [];
      for (var i = 0; i < list.length; i++) {
        var access = list[i];
        var user = this.resolveMemberUser(access);
        var userId = this.resolveMemberUserId(access);
        if (!user && userId == null) {
          continue;
        }
        if (
          this.isAccessCurrentUser(access) ||
          (user &&
            (this.isSameUserId(user.uid, this.currentUserId) ||
              this.isCurrentUserIdentity(user.uid) ||
              this.isCurrentUserIdentity(user.id))) ||
          this.isCurrentUserIdentity(userId)
        ) {
          if (userId != null) {
            this.rememberMyChatUserId(userId);
          }
          return;
        }
      }
    },
    isConversationOwner(conversation, members) {
      if (!conversation) {
        return false;
      }
      var list =
        members ||
        this.membersMap[conversation.id] ||
        [];
      var ownerAccess = this.findOwnerAccess(list, conversation);
      if (ownerAccess && this.isAccessCurrentUser(ownerAccess)) {
        var ownerUid = this.resolveMemberUserId(ownerAccess);
        if (ownerUid != null) {
          this.rememberMyChatUserId(ownerUid);
        }
        return true;
      }
      if (
        conversation.user &&
        conversation.user.uid != null &&
        this.isSameUserId(conversation.user.uid, this.currentUserId)
      ) {
        if (conversation.userId != null) {
          this.rememberMyChatUserId(conversation.userId);
        }
        return true;
      }
      var myId = this.resolveMyChatUserId();
      if (
        conversation.userId != null &&
        this.isSameUserId(conversation.userId, myId)
      ) {
        return true;
      }
      var remembered = this.getRememberedGroup(conversation.id);
      if (remembered) {
        if (remembered.createdByMe) {
          return true;
        }
        if (
          remembered.ownerId != null &&
          this.isSameUserId(remembered.ownerId, myId)
        ) {
          return true;
        }
      }
      return false;
    },
    maybeRememberMyChatUserIdFromConversation(mapped) {
      if (
        mapped &&
        mapped.userId != null &&
        mapped.user &&
        mapped.user.uid != null &&
        this.isSameUserId(mapped.user.uid, this.currentUserId)
      ) {
        this.rememberMyChatUserId(mapped.userId);
      }
    },
    findOtherMember(members) {
      var list = members || [];
      for (var i = 0; i < list.length; i++) {
        if (!this.isAccessCurrentUser(list[i])) {
          return list[i];
        }
      }
      return list[0] || null;
    },
    resolveMemberUser(access) {
      if (!access) {
        return null;
      }
      if (access.user && typeof access.user === "object") {
        return access.user;
      }
      // Flat user payload from chatUserAccess
      if (
        access.user_id == null &&
        (access.name != null ||
          access.firstname != null ||
          access.uid != null)
      ) {
        return access;
      }
      return null;
    },
    resolveMemberUserId(access) {
      if (!access) {
        return null;
      }
      if (access.user_id != null) {
        return access.user_id;
      }
      if (access.user && access.user.id != null) {
        return access.user.id;
      }
      if (
        access.name != null ||
        access.firstname != null ||
        access.uid != null
      ) {
        return access.id != null ? access.id : null;
      }
      return null;
    },
    normalizeAccessRows(rows) {
      var self = this;
      return (rows || [])
        .map(function (row) {
          return self.normalizeAccessRow(row);
        })
        .filter(Boolean);
    },
    normalizeAccessRow(row) {
      if (!row) {
        return null;
      }
      // Classic access row: nested user and/or user_id
      if (row.user || row.user_id != null) {
        return row;
      }
      // New flat user payload: id is the user id (not access-row id)
      if (
        row.name != null ||
        row.firstname != null ||
        row.uid != null
      ) {
        return {
          user_id: row.id,
          user: {
            id: row.id,
            firstname: row.firstname,
            lastname: row.lastname,
            name: row.name,
            uid: row.uid,
            avatar: row.avatar,
          },
        };
      }
      return row;
    },
    toggleMembersPanel() {
      if (this.isPendingSelection) {
        return;
      }
      var c = this.selectedConversation;
      if (c && c.type === "personal") {
        this.membersPanelOpen = false;
        return;
      }
      // Group/channel: only owner manages members
      if (
        c &&
        (c.type === "group" || c.type === "channel") &&
        !this.canCloseGroup
      ) {
        this.membersPanelOpen = false;
        return;
      }
      this.membersPanelOpen = !this.membersPanelOpen;
      if (this.membersPanelOpen && this.selectedId != null) {
        this.loadMembers(this.selectedId, true);
      }
    },
    addMember(user) {
      var self = this;
      if (!user || !this.selectedId || this.membersBusy || this.isPendingSelection) {
        return;
      }
      this.membersBusy = true;
      this.$axios
        .post(this.$endpoint + "chatUserAccess", {
          chat_id: this.selectedId,
          user_id: user.id,
        })
        .then(function () {
          return self.loadMembers(self.selectedId, true);
        })
        .catch(function (error) {
          console.error("add chat member error:", error);
        })
        .finally(function () {
          self.membersBusy = false;
        });
    },
    hasMemberUserId(members, userId) {
      if (userId == null) {
        return false;
      }
      var list = members || [];
      for (var i = 0; i < list.length; i++) {
        var uid = this.resolveMemberUserId(list[i]);
        if (this.isSameUserId(uid, userId)) {
          return true;
        }
      }
      return false;
    },
    ensurePersonalPeerAccess(chatId) {
      var self = this;
      var conversation = this.selectedConversation;
      if (
        chatId == null ||
        !conversation ||
        conversation.type !== "personal"
      ) {
        return Promise.resolve();
      }

      var knownPeerId = conversation.peerUserId;
      return this.loadMembers(chatId, true)
        .then(function () {
          var members = self.membersMap[chatId] || [];
          var peerId =
            knownPeerId != null
              ? knownPeerId
              : self.resolvePersonalPeerId(conversation, members);

          if (peerId == null) {
            return;
          }
          if (conversation.peerUserId == null) {
            self.$set(conversation, "peerUserId", peerId);
            self.rememberPersonalPeer(conversation);
          }

          // Intact 1:1 chat — both sides already have access
          if (
            members.length >= 2 &&
            self.hasMemberUserId(members, peerId)
          ) {
            return;
          }
          if (self.hasMemberUserId(members, peerId)) {
            return;
          }

          return self.$axios
            .post(self.$endpoint + "chatUserAccess", {
              chat_id: chatId,
              user_id: peerId,
            })
            .then(function () {
              return self.loadMembers(chatId, true);
            })
            .catch(function (error) {
              // Peer may already have access; still allow send
              console.error("restore personal peer access error:", error);
            });
        })
        .catch(function (error) {
          console.error("ensure personal peer access error:", error);
        });
    },
    removeMember(access) {
      var self = this;
      if (
        !access ||
        !this.selectedId ||
        this.membersBusy ||
        this.isPendingSelection
      ) {
        return;
      }
      var ownerId = this.selectedOwnerId;
      var uid = this.resolveMemberUserId(access);
      if (ownerId != null && Number(uid) === Number(ownerId)) {
        return;
      }

      this.membersBusy = true;
      var request;
      // Flat API uses user id as `id`; only delete by access-id when it differs
      var accessId = access.id;
      var canDeleteByAccessId =
        accessId != null &&
        uid != null &&
        Number(accessId) !== Number(uid) &&
        (access.user != null || access.user_id != null);
      if (canDeleteByAccessId) {
        request = this.$axios.delete(
          this.$endpoint + "chatUserAccess/" + accessId
        );
      } else {
        request = this.$axios.delete(
          this.$endpoint + "chatUserAccess/by-user",
          {
            data: {
              chat_id: this.selectedId,
              user_id: uid,
            },
          }
        );
      }

      request
        .then(function () {
          return self.loadMembers(self.selectedId, true);
        })
        .catch(function (error) {
          console.error("remove chat member error:", error);
        })
        .finally(function () {
          self.membersBusy = false;
        });
    },
    closeSelectedChat() {
      if (!this.canCloseGroup) {
        return;
      }
      this.updateChatState({ closeChat: true }, "close");
    },
    reopenSelectedChat() {
      if (!this.canCloseGroup) {
        return;
      }
      this.updateChatState({ reopenChat: true }, "open");
    },
    updateChatState(payload, nextState) {
      var self = this;
      if (!this.selectedId || this.stateBusy || this.isPendingSelection) {
        return;
      }
      this.stateBusy = true;
      this.$axios
        .put(this.$endpoint + "chat/" + this.selectedId, payload)
        .then(function () {
          var conversation = self.selectedConversation;
          if (conversation) {
            conversation.state = nextState;
          }
        })
        .catch(function (error) {
          console.error("update chat state error:", error);
        })
        .finally(function () {
          self.stateBusy = false;
        });
    },
    handleSend(payload) {
      var self = this;
      if (!payload || this.isSelectedClosed || this.isChannelSubscriberOnly) {
        return;
      }
      var text = typeof payload === "string" ? payload : payload.text || "";
      text = text ? String(text).trim() : "";
      var files =
        payload && Array.isArray(payload.files) ? payload.files.slice() : [];
      var replyToId =
        payload && payload.replyToMessageId != null
          ? payload.replyToMessageId
          : null;
      var mentionUserIds =
        payload && Array.isArray(payload.mentionUserIds)
          ? payload.mentionUserIds
          : [];
      var forwardMessageId =
        payload && payload.forwardMessageId != null
          ? payload.forwardMessageId
          : null;

      if (!text && !files.length && !forwardMessageId) {
        return;
      }

      if (files.length && this.hasOversizedChatFiles(files)) {
        this.showChatFileSizeError();
        return;
      }

      if (this.isPendingSelection) {
        this.sendFirstMessageForPending(text, files);
        return;
      }

      if (!this.selectedId) {
        return;
      }

      var messageText = text;
      if (!messageText && files.length) {
        messageText = "no caption";
      }

      var body = {
        chat_id: this.selectedId,
      };
      if (messageText) {
        body.message = messageText;
      }
      if (replyToId != null) {
        body.reply_to_message_id = replyToId;
      }
      if (mentionUserIds.length) {
        body.mention_user_ids = mentionUserIds;
      }
      if (forwardMessageId != null) {
        body.forward_message_id = forwardMessageId;
      }

      var chatId = this.selectedId;
      var tempId =
        "temp-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);
      var localAttachments = this.mapOptimisticAttachments(
        payload && payload.attachments
      );
      var optimistic = this.buildOptimisticMessage({
        tempId: tempId,
        chatId: chatId,
        text: text,
        displayText: messageText === "no caption" ? "" : text,
        replyToId: replyToId,
        mentionUserIds: mentionUserIds,
        attachments: localAttachments,
      });
      this.appendOptimisticMessage(chatId, optimistic);

      this.ensurePersonalPeerAccess(chatId)
        .then(function () {
          return self.$axios.post(self.$endpoint + "chatMessage", body);
        })
        .then(function (response) {
          var resBody = response && response.data ? response.data : {};
          var row = resBody.data || null;
          var messageId = row && row.id != null ? row.id : null;

          if (files.length && messageId != null) {
            return self
              .uploadChatMessageFiles(messageId, files)
              .then(function () {
                self.removeMessageById(chatId, tempId);
                return self.loadMessages(chatId, true);
              });
          }

          var mapped = row
            ? self.mapMessage(row, chatId)
            : {
                id: Date.now(),
                text: text,
                mine: true,
                sender: self.$t("conversation-me"),
                time: self.nowLabel(),
                attachments: localAttachments,
                readers: [],
                readersCount: 0,
              };
          mapped.mine = true;
          mapped.sender = self.$t("conversation-me");
          mapped.pending = false;
          mapped.failed = false;
          if (
            (!mapped.attachments || !mapped.attachments.length) &&
            localAttachments.length
          ) {
            mapped.attachments = localAttachments;
          }
          var sentUserId =
            row &&
            (row.user_id != null
              ? row.user_id
              : row.user && row.user.id != null
                ? row.user.id
                : null);
          if (sentUserId != null) {
            self.rememberMyChatUserId(sentUserId);
          }

          self.replaceOptimisticMessage(chatId, tempId, mapped);

          var conversation = self.selectedConversation;
          if (conversation) {
            conversation.preview = mapped.text || text;
            conversation.time = mapped.time || self.nowLabel();
          }
        })
        .catch(function (error) {
          console.error("send chat message error:", error);
          self.markOptimisticMessageFailed(chatId, tempId);
        });
    },
    mapOptimisticAttachments(items) {
      if (!Array.isArray(items)) {
        return [];
      }
      return items.map(function (item, index) {
        return {
          id: "local-" + index,
          name: item.name || "file",
          type: item.type || "",
          preview: item.preview || "",
          extension: "",
          size: null,
        };
      });
    },
    buildOptimisticMessage(options) {
      var chatId = options.chatId;
      var replyTo = null;
      if (options.replyToId != null) {
        var replySrc = (this.messagesMap[chatId] || []).find(function (item) {
          return Number(item.id) === Number(options.replyToId);
        });
        if (replySrc) {
          replyTo = {
            id: replySrc.id,
            text: replySrc.text || "",
            sender: replySrc.sender || "",
            deleted: false,
          };
        }
      }
      var mentionIds = Array.isArray(options.mentionUserIds)
        ? options.mentionUserIds
        : [];
      return {
        id: options.tempId,
        tempId: options.tempId,
        text: options.displayText != null ? options.displayText : options.text || "",
        mine: true,
        pending: true,
        failed: false,
        sender: this.$t("conversation-me"),
        time: this.nowLabel(),
        userId: this.resolveMyChatUserId(),
        avatarUrl: "",
        avatarColor: "#3b82f6",
        initials: "",
        isReadByMe: true,
        isEdited: false,
        readers: [],
        readersCount: 0,
        attachments: options.attachments || [],
        replyTo: replyTo,
        forwardedFrom: null,
        mentions: mentionIds.map(function (id) {
          return { userId: id, name: "" };
        }),
      };
    },
    appendOptimisticMessage(chatId, message) {
      if (chatId == null || !message) {
        return;
      }
      var list = this.messagesMap[chatId]
        ? this.messagesMap[chatId].slice()
        : [];
      list.push(message);
      this.$set(this.messagesMap, chatId, list);
      var conversation = this.findConversationById(chatId);
      if (conversation) {
        conversation.preview = message.text || conversation.preview;
        conversation.time = message.time || this.nowLabel();
      }
    },
    replaceOptimisticMessage(chatId, tempId, mapped) {
      if (chatId == null || tempId == null || !mapped) {
        return;
      }
      var list = this.messagesMap[chatId]
        ? this.messagesMap[chatId].slice()
        : [];
      var index = list.findIndex(function (item) {
        return (
          item &&
          (String(item.id) === String(tempId) ||
            String(item.tempId) === String(tempId))
        );
      });
      if (index > -1) {
        var existingReal = list.findIndex(function (item, i) {
          return (
            i !== index &&
            item &&
            !item.pending &&
            Number(item.id) === Number(mapped.id)
          );
        });
        if (existingReal > -1) {
          list.splice(index, 1);
        } else {
          this.$set(list, index, mapped);
        }
      } else {
        var byId = list.findIndex(function (item) {
          return Number(item.id) === Number(mapped.id);
        });
        if (byId > -1) {
          this.$set(list, byId, Object.assign({}, list[byId], mapped, {
            pending: false,
            failed: false,
          }));
        } else {
          list.push(mapped);
        }
      }
      this.$set(this.messagesMap, chatId, list);
    },
    markOptimisticMessageFailed(chatId, tempId) {
      if (chatId == null || tempId == null || !this.messagesMap[chatId]) {
        return;
      }
      var list = this.messagesMap[chatId].slice();
      var index = list.findIndex(function (item) {
        return (
          item &&
          (String(item.id) === String(tempId) ||
            String(item.tempId) === String(tempId))
        );
      });
      if (index < 0) {
        return;
      }
      this.$set(
        list,
        index,
        Object.assign({}, list[index], {
          pending: false,
          failed: true,
        })
      );
      this.$set(this.messagesMap, chatId, list);
    },
    removeMessageById(chatId, messageId) {
      if (chatId == null || messageId == null || !this.messagesMap[chatId]) {
        return;
      }
      var list = this.messagesMap[chatId].filter(function (item) {
        return (
          item &&
          String(item.id) !== String(messageId) &&
          String(item.tempId || "") !== String(messageId)
        );
      });
      this.$set(this.messagesMap, chatId, list);
    },
    hasOversizedChatFiles(files) {
      if (!files || !files.length) {
        return false;
      }
      var maxSize = 1 * 1024 * 1024;
      return files.some(function (file) {
        return file && file.size > maxSize;
      });
    },
    showChatFileSizeError() {
      var chat = this.$refs.conversationChat;
      if (chat && typeof chat.showChatFileSizeError === "function") {
        chat.showChatFileSizeError();
        return;
      }
      var message =
        this.$te && this.$te("File size must be less than 1 MB")
          ? this.$t("File size must be less than 1 MB")
          : "حجم فایل نباید بیشتر از 1 MB باشد";
      if (this.$toast && typeof this.$toast.error === "function") {
        this.$toast.error(message);
        return;
      }
      console.error(message);
    },
    uploadChatMessageFiles(messageId, files) {
      if (messageId == null || !files || !files.length) {
        return Promise.resolve();
      }
      var formData = new FormData();
      formData.append("select", "fileUpload");
      formData.append("type", "chat");
      formData.append("refId", messageId);
      files.forEach(function (file, index) {
        formData.append("file[" + index + "]", file);
      });
      return this.$axios.post(this.$endpoint + "createdetail", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    mapMessageAttachments(row) {
      if (!row) {
        return [];
      }
      var files = row.files || row.attachments || row.details || [];
      if (!Array.isArray(files)) {
        return [];
      }
      var imageExts = {
        png: "image/png",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        gif: "image/gif",
        webp: "image/webp",
        bmp: "image/bmp",
        svg: "image/svg+xml",
      };
      return files.map(function (file) {
        var name =
          file.title || file.name || file.original_name || file.filename || "file";
        var ext = String(file.extension || "").toLowerCase();
        if (ext && ext.charAt(0) !== ".") {
          ext = "." + ext;
        }
        if (!ext && name.indexOf(".") > -1) {
          ext = name.slice(name.lastIndexOf(".")).toLowerCase();
        }
        var link = file.link || file.url || file.path || file.preview || "";
        if (!ext && link) {
          var linkPath = String(link).split("?")[0];
          var linkDot = linkPath.lastIndexOf(".");
          if (linkDot > -1) {
            ext = linkPath.slice(linkDot).toLowerCase();
          }
        }
        var clean = ext.replace(/^\./, "");
        var mimeFromExt = imageExts[clean] || "";
        var rawType = file.mime || "";
        // API often sends type: "chat" (business type) — only trust real mime values
        if (!rawType && file.type && String(file.type).indexOf("/") > -1) {
          rawType = file.type;
        }
        var type = rawType;
        if (mimeFromExt) {
          type = mimeFromExt;
        } else if (!type || String(type).indexOf("/") === -1) {
          type = clean ? "file/" + clean : type || "";
        }
        return {
          id: file.id != null ? file.id : file._id,
          name: name,
          type: type,
          preview: link,
          extension: ext,
          size: file.size != null ? file.size : null,
        };
      });
    },
    handleEditMessage(payload) {
      var self = this;
      if (!payload || !payload.id || this.sending) {
        return;
      }
      var text = payload.text ? String(payload.text).trim() : "";
      var files =
        payload && Array.isArray(payload.files) ? payload.files.slice() : [];
      if (!text && !files.length) {
        return;
      }
      if (files.length && this.hasOversizedChatFiles(files)) {
        this.showChatFileSizeError();
        return;
      }
      var messageText = text;
      if (!messageText && files.length) {
        messageText = "no caption";
      }
      var body = { message: messageText };
      if (Array.isArray(payload.mentionUserIds)) {
        body.mention_user_ids = payload.mentionUserIds;
      }
      var chatId = this.selectedId;
      var messageId = payload.id;
      this.sending = true;
      this.$axios
        .put(this.$endpoint + "chatMessage/" + messageId, body)
        .then(function (response) {
          var resBody = response && response.data ? response.data : {};
          var row = resBody.data || resBody;

          if (files.length) {
            return self
              .uploadChatMessageFiles(messageId, files)
              .then(function () {
                return self.loadMessages(chatId, true);
              });
          }

          var mapped = self.mapMessage(row, chatId);
          mapped.isEdited = true;
          var list = (self.messagesMap[chatId] || []).slice();
          var idx = list.findIndex(function (m) {
            return Number(m.id) === Number(messageId);
          });
          if (idx > -1) {
            list.splice(idx, 1, mapped);
            self.$set(self.messagesMap, chatId, list);
          }
        })
        .catch(function (error) {
          console.error("edit chat message error:", error);
        })
        .finally(function () {
          self.sending = false;
        });
    },
    handleDeleteMessage(message) {
      var self = this;
      if (!message || !message.id || this.sending) {
        return;
      }
      this.sending = true;
      this.$axios
        .delete(this.$endpoint + "chatMessage/" + message.id)
        .then(function () {
          var list = (self.messagesMap[self.selectedId] || []).filter(
            function (m) {
              return Number(m.id) !== Number(message.id);
            }
          );
          self.$set(self.messagesMap, self.selectedId, list);
        })
        .catch(function (error) {
          console.error("delete chat message error:", error);
        })
        .finally(function () {
          self.sending = false;
        });
    },
    handleForwardMessage(payload) {
      var self = this;
      if (
        !payload ||
        payload.messageId == null ||
        payload.chatId == null ||
        this.sending
      ) {
        return;
      }
      this.sending = true;
      this.$axios
        .post(this.$endpoint + "chatMessage", {
          chat_id: payload.chatId,
          forward_message_id: payload.messageId,
          message: payload.caption || undefined,
        })
        .then(function () {
          if (Number(payload.chatId) === Number(self.selectedId)) {
            return self.loadMessages(self.selectedId, true);
          }
        })
        .catch(function (error) {
          console.error("forward chat message error:", error);
        })
        .finally(function () {
          self.sending = false;
        });
    },
    deleteSelectedChat() {
      var self = this;
      if (!this.canDeleteChat || !this.selectedId || this.stateBusy) {
        return;
      }
      var chatId = this.selectedId;
      var conversation = this.selectedConversation;
      if (conversation && conversation.type === "personal") {
        this.leavePersonalChat(chatId);
        return;
      }
      this.stateBusy = true;
      this.$axios
        .delete(this.$endpoint + "chat/" + chatId)
        .then(function () {
          self.removeConversationLocally(chatId);
        })
        .catch(function (error) {
          console.error("delete chat error:", error);
        })
        .finally(function () {
          self.stateBusy = false;
        });
    },
    removeConversationLocally(chatId) {
      this.conversations = (this.conversations || []).filter(function (item) {
        return Number(item.id) !== Number(chatId);
      });
      this.$delete(this.messagesMap, chatId);
      this.$delete(this.membersMap, chatId);
      this.clearChatUnreadMeta(chatId);
      if (Number(this.selectedId) === Number(chatId)) {
        this.selectedId = null;
      }
      this.membersPanelOpen = false;
    },
    refreshTabUnreadCounts() {
      var next = {
        personal: 0,
        group: 0,
        channel: 0,
        ticket: 0,
      };
      var meta = this.chatUnreadMeta || {};
      Object.keys(meta).forEach(function (id) {
        var row = meta[id];
        if (!row || !next.hasOwnProperty(row.type)) {
          return;
        }
        next[row.type] += Number(row.unread) || 0;
      });
      this.tabUnreadCounts = next;
      // Keep AppBar badge aligned with tab message sums (not API total_unread).
      if (this.$store) {
        this.$store.commit(
          "SET_CHAT_UNREAD_COUNT",
          next.personal + next.group + next.channel + next.ticket
        );
      }
    },
    setChatUnreadMeta(chatId, unread, type) {
      if (chatId == null || !type) {
        return;
      }
      var key = String(chatId);
      var nextUnread = Math.max(0, Number(unread) || 0);
      var prev = this.chatUnreadMeta[key];
      if (
        prev &&
        Number(prev.unread) === nextUnread &&
        prev.type === type
      ) {
        return;
      }
      this.$set(this.chatUnreadMeta, key, {
        unread: nextUnread,
        type: type,
      });
      this.refreshTabUnreadCounts();
    },
    clearChatUnreadMeta(chatId) {
      if (chatId == null) {
        return;
      }
      var key = String(chatId);
      if (!this.chatUnreadMeta[key]) {
        return;
      }
      this.$delete(this.chatUnreadMeta, key);
      this.refreshTabUnreadCounts();
    },
    syncChatUnreadMetaFromList(list, pruneType) {
      var self = this;
      var pruneTypes = Array.isArray(pruneType)
        ? pruneType
        : pruneType
          ? [pruneType]
          : [];
      if (pruneTypes.length) {
        Object.keys(this.chatUnreadMeta || {}).forEach(function (id) {
          var row = self.chatUnreadMeta[id];
          if (row && pruneTypes.indexOf(row.type) !== -1) {
            self.$delete(self.chatUnreadMeta, id);
          }
        });
      }
      (list || []).forEach(function (item) {
        if (!item || item.id == null || item.isPending) {
          return;
        }
        if (
          item.type !== "personal" &&
          item.type !== "group" &&
          item.type !== "channel" &&
          item.type !== "ticket"
        ) {
          return;
        }
        self.$set(self.chatUnreadMeta, String(item.id), {
          unread: Number(item.unread) || 0,
          type: item.type,
        });
      });
      this.refreshTabUnreadCounts();
    },
    bootstrapTabUnreadCounts() {
      var self = this;
      var types = ["personal", "group", "channel", "ticket"];
      types.forEach(function (type) {
        if (type === self.activeTab) {
          return;
        }
        var params = {
          options: JSON.stringify({
            page: 1,
            itemsPerPage: 50,
            sortDesc: [true],
          }),
          sort: "newest_message",
          type: self.toApiChatType(type) || type,
        };
        self.$axios
          .get(self.$endpoint + "chat", { params: params })
          .then(function (response) {
            var rows = self.extractList(response);
            var mapped = rows.map(function (row) {
              return self.mapChatToConversation(
                row,
                type === "ticket" ? "ticket" : type
              );
            });
            self.syncChatUnreadMetaFromList(mapped, type);
          })
          .catch(function () {});
      });
    },
    findMyAccessInMembers(members) {
      var list = members || [];
      for (var i = 0; i < list.length; i++) {
        if (this.isAccessCurrentUser(list[i])) {
          return list[i];
        }
      }
      return null;
    },
    deleteOwnChatAccess(chatId, access) {
      if (access) {
        var uid = this.resolveMemberUserId(access);
        var accessId = access.id;
        var canDeleteByAccessId =
          accessId != null &&
          uid != null &&
          Number(accessId) !== Number(uid) &&
          (access.user != null || access.user_id != null);
        if (canDeleteByAccessId) {
          return this.$axios.delete(
            this.$endpoint + "chatUserAccess/" + accessId
          );
        }
        if (uid != null) {
          return this.$axios.delete(
            this.$endpoint + "chatUserAccess/by-user",
            {
              data: {
                chat_id: chatId,
                user_id: uid,
              },
            }
          );
        }
      }
      var myId = this.resolveMyChatUserId();
      if (myId == null) {
        return Promise.reject(new Error("missing current user id"));
      }
      return this.$axios.delete(this.$endpoint + "chatUserAccess/by-user", {
        data: {
          chat_id: chatId,
          user_id: myId,
        },
      });
    },
    leavePersonalChat(chatId) {
      var self = this;
      if (chatId == null || this.stateBusy) {
        return;
      }
      this.stateBusy = true;
      this.loadMembers(chatId, true)
        .then(function () {
          var mine = self.findMyAccessInMembers(self.membersMap[chatId] || []);
          return self.deleteOwnChatAccess(chatId, mine);
        })
        .then(function () {
          self.removeConversationLocally(chatId);
        })
        .catch(function (error) {
          console.error("leave chat access error:", error);
        })
        .finally(function () {
          self.stateBusy = false;
        });
    },
    leaveSelectedGroup() {
      if (!this.canLeaveGroup || !this.selectedId || this.stateBusy) {
        return;
      }
      this.leavePersonalChat(this.selectedId);
    },
    openMentionsPanel() {
      this.mentionsPanelOpen = true;
      this.fetchMentionsList();
    },
    openUserFromMention(payload) {
      var self = this;
      var userId = payload && payload.userId != null ? payload.userId : null;
      var name = payload && payload.name ? String(payload.name).trim() : "";
      if (userId == null) {
        return;
      }

      this.resolvePersonalConversationByPeer(userId)
        .then(function (existing) {
          if (existing) {
            return self.openExistingPersonalChat(existing);
          }

          // Enrich from partners when possible, but do not block opening a DM
          // for someone already visible in the current chat (ticket/group/etc).
          return self.findPartnerUser(userId, name).then(function (found) {
            return self
              .resolvePersonalConversationByPeer(userId)
              .then(function (again) {
                if (again) {
                  return self.openExistingPersonalChat(again);
                }
                var user = self.buildOpenChatUser(found, userId, name);
                self.createLocalPersonalPending(user);
                return null;
              });
          });
        })
        .catch(function (error) {
          console.error("open mention user chat error:", error);
        });
    },
    fetchMentionsCount() {
      var self = this;
      this.$axios
        .get(this.$endpoint + "chatMessage/my-mentions", {
          params: {
            options: JSON.stringify({ page: 1, itemsPerPage: 1 }),
          },
        })
        .then(function (response) {
          var body = response && response.data ? response.data : {};
          var total =
            body.total != null
              ? body.total
              : body.data && body.data.total != null
                ? body.data.total
                : Array.isArray(body.data)
                  ? body.data.length
                  : 0;
          self.mentionsCount = Number(total) || 0;
        })
        .catch(function () {});
    },
    fetchMentionsList() {
      var self = this;
      this.mentionsLoading = true;
      this.$axios
        .get(this.$endpoint + "chatMessage/my-mentions", {
          params: {
            options: JSON.stringify({
              page: 1,
              itemsPerPage: 50,
              sortDesc: [true],
            }),
          },
        })
        .then(function (response) {
          var rows = self.extractMessageList(response);
          self.mentionsList = rows
            .map(function (row) {
              return self.mapMentionListItem(row);
            })
            .filter(Boolean);
          self.mentionsCount = self.mentionsList.length;
        })
        .catch(function (error) {
          console.error("my mentions error:", error);
          self.mentionsList = [];
        })
        .finally(function () {
          self.mentionsLoading = false;
        });
    },
    mapMentionListItem(row) {
      if (!row) {
        return null;
      }
      var messageRow = row;
      var text = "";
      var rawMessage = row.message;

      if (
        rawMessage &&
        typeof rawMessage === "object" &&
        !Array.isArray(rawMessage)
      ) {
        text = rawMessage.message || rawMessage.text || "";
        messageRow = Object.assign({}, row, rawMessage, {
          chat: row.chat || rawMessage.chat || null,
        });
      } else if (typeof rawMessage === "string") {
        var trimmed = rawMessage.trim();
        if (trimmed.charAt(0) === "{") {
          try {
            var parsed = JSON.parse(trimmed);
            if (parsed && typeof parsed === "object") {
              text = parsed.message || parsed.text || "";
              messageRow = Object.assign({}, row, parsed, {
                chat: row.chat || parsed.chat || null,
              });
            } else {
              text = rawMessage;
            }
          } catch (e) {
            text = rawMessage;
          }
        } else {
          text = rawMessage;
        }
      }

      if (!text && typeof messageRow.message === "string") {
        var nested = messageRow.message.trim();
        if (nested.charAt(0) !== "{") {
          text = messageRow.message;
        }
      }

      var chat = messageRow.chat || row.chat || null;
      var chatId =
        messageRow.chat_id != null
          ? messageRow.chat_id
          : row.chat_id != null
            ? row.chat_id
            : chat && chat.id;

      var mentionsRaw = Array.isArray(messageRow.mentions)
        ? messageRow.mentions
        : Array.isArray(row.mentions)
          ? row.mentions
          : [];
      var mentionNames = [];
      for (var mi = 0; mi < mentionsRaw.length; mi++) {
        var m = mentionsRaw[mi];
        if (!m) {
          continue;
        }
        var mentionName = "";
        if (m.user) {
          mentionName = [m.user.firstname, m.user.lastname]
            .filter(Boolean)
            .join(" ");
        } else if (m.name) {
          mentionName = String(m.name).trim();
        }
        if (mentionName) {
          mentionNames.push(mentionName);
        }
      }

      var chatTitle = this.resolveMentionChatTitle(chat, chatId);
      var messageId =
        messageRow && messageRow.id != null
          ? messageRow.id
          : row && row.message && row.message.id != null
            ? row.message.id
            : row.id;

      return {
        id: messageId,
        chatId: chatId,
        chatTitle: chatTitle,
        text: String(text || "").trim(),
        time: messageRow.time || row.time || "",
        mentionNames: mentionNames,
        initial: this.mentionItemInitial(chatTitle, chatId),
      };
    },
    mentionItemInitial(title, chatId) {
      var raw = String(title || "").trim();
      if (raw) {
        return raw.charAt(0);
      }
      return chatId != null ? String(chatId).charAt(0) : "@";
    },
    mentionAvatarColor(item) {
      var palette = [
        "#5B8DEF",
        "#4DB6AC",
        "#7E57C2",
        "#EF6C00",
        "#EC407A",
        "#26A69A",
        "#42A5F5",
        "#AB47BC",
      ];
      var seed = Number(item && item.chatId) || 0;
      if (!seed && item && item.chatTitle) {
        seed = String(item.chatTitle)
          .split("")
          .reduce(function (sum, ch) {
            return sum + ch.charCodeAt(0);
          }, 0);
      }
      return palette[Math.abs(seed) % palette.length];
    },
    mentionPreviewParts(text, names) {
      var value = String(text || "");
      if (!value) {
        return [{ text: "", mention: false }];
      }
      var nameList = Array.isArray(names)
        ? names
            .map(function (n) {
              return String(n || "").trim();
            })
            .filter(Boolean)
        : [];
      var re = null;
      if (nameList.length) {
        nameList.sort(function (a, b) {
          return b.length - a.length;
        });
        var pattern = nameList
          .map(
            function (n) {
              return "@" + this.escapeRegExp(n);
            }.bind(this)
          )
          .join("|");
        re = new RegExp("(" + pattern + ")", "g");
      } else {
        // Fallback: @ + up to 3 name tokens (supports "First Last")
        re = /@[^\s@]+(?:\s+[^\s@]+){0,2}(?=\s|$|[،,.:;!?؟])/g;
      }
      var parts = [];
      var last = 0;
      var match;
      while ((match = re.exec(value))) {
        if (match.index > last) {
          parts.push({
            text: value.slice(last, match.index),
            mention: false,
          });
        }
        parts.push({ text: match[0], mention: true });
        last = match.index + match[0].length;
      }
      if (last < value.length) {
        parts.push({ text: value.slice(last), mention: false });
      }
      return parts.length ? parts : [{ text: value, mention: false }];
    },
    escapeRegExp(value) {
      return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    resolveMentionChatTitle(chat, chatId) {
      if (chat) {
        try {
          var mapped = this.mapChatToConversation(chat);
          if (mapped) {
            if (mapped.type === "ticket" && mapped.ticketId != null) {
              return mapped.title
                ? "#" + mapped.ticketId + " " + mapped.title
                : "#" + mapped.ticketId;
            }
            if (mapped.title) {
              return mapped.title;
            }
          }
        } catch (e) {
          if (chat.name || chat.title) {
            return chat.name || chat.title;
          }
        }
      }
      if (chatId != null) {
        var existing = this.conversations.find(function (c) {
          return Number(c.id) === Number(chatId);
        });
        if (existing) {
          if (existing.type === "ticket" && existing.ticketId != null) {
            return existing.title
              ? "#" + existing.ticketId + " " + existing.title
              : "#" + existing.ticketId;
          }
          if (existing.title) {
            return existing.title;
          }
        }
        return "#" + chatId;
      }
      return "";
    },
    openMentionItem(item) {
      if (!item || item.chatId == null) {
        return;
      }
      var messageId = item.id != null ? item.id : null;
      this.mentionsPanelOpen = false;
      var self = this;
      var found = this.conversations.find(function (c) {
        return Number(c.id) === Number(item.chatId);
      });

      var openThenScroll = function (conversation) {
        if (messageId != null) {
          var chatRef = self.$refs.conversationChat;
          if (chatRef) {
            chatRef.pendingScrollMessageId = messageId;
          }
        }
        return self.selectConversation(conversation).then(function () {
          if (messageId == null) {
            return;
          }
          self.$nextTick(function () {
            self.scrollChatToMessage(messageId);
          });
        });
      };

      if (found) {
        openThenScroll(found);
        return;
      }
      this.$axios
        .get(this.$endpoint + "chat/" + item.chatId)
        .then(function (response) {
          var chat = response && response.data ? response.data : null;
          if (chat && chat.data) {
            chat = chat.data;
          }
          if (!chat) {
            return;
          }
          var mapped = self.upsertConversation(chat);
          return openThenScroll(mapped);
        })
        .catch(function (error) {
          console.error("open mention chat error:", error);
        });
    },
    scrollChatToMessage(messageId) {
      var chat = this.$refs.conversationChat;
      if (!chat || typeof chat.scrollToMessage !== "function") {
        return;
      }
      chat.scrollToMessage(messageId);
    },
    sendFirstMessageForPending(text, files) {
      var self = this;
      var pending = this.pendingConversation;
      if (!pending) {
        return;
      }

      files = Array.isArray(files) ? files : [];
      var messageText = text ? String(text).trim() : "";
      if (!messageText && files.length) {
        messageText = "no caption";
      }
      if (!messageText) {
        return;
      }
      if (files.length && this.hasOversizedChatFiles(files)) {
        this.showChatFileSizeError();
        return;
      }

      // Personal draft: create chat + first message together
      if (
        pending.type === "personal" &&
        Array.isArray(pending.userIds) &&
        pending.userIds.length
      ) {
        this.sendFirstMessageForPersonalPending(messageText, files, text);
        return;
      }

      if (!pending.refType || pending.refId == null) {
        return;
      }

      this.sending = true;
      this.createChatForRef(pending.refType, pending.refId, messageText)
        .then(function (result) {
          var chat = result && result.chat;
          if (!chat) {
            return;
          }
          var mapped = self.mapChatToConversation(chat, pending.type);
          if (pending.title) {
            mapped.title = pending.title;
          }
          mapped.preview = text || messageText;
          mapped.time = self.nowLabel();
          self.upsertMappedConversation(mapped);
          self.pendingConversation = null;
          self.selectedId = mapped.id;

          var firstMessage = result.firstMessage || null;
          var messageId =
            firstMessage && firstMessage.id != null ? firstMessage.id : null;

          if (files.length && messageId != null) {
            return self
              .uploadChatMessageFiles(messageId, files)
              .then(function () {
                return self.loadMessages(mapped.id, true);
              })
              .then(function () {
                return self.loadMembers(mapped.id, true);
              });
          }

          var first = firstMessage
            ? self.mapMessage(firstMessage, mapped.id)
            : {
                id: Date.now(),
                text: messageText,
                mine: true,
                sender: self.$t("conversation-me"),
                time: self.nowLabel(),
                attachments: [],
              };
          self.$set(self.messagesMap, mapped.id, [first]);
          return self.loadMembers(mapped.id, true);
        })
        .catch(function (error) {
          console.error("create chat on first message error:", error);
        })
        .finally(function () {
          self.sending = false;
        });
    },
    sendFirstMessageForPersonalPending(messageText, files, previewText) {
      var self = this;
      var pending = this.pendingConversation;
      if (!pending || !pending.userIds || !pending.userIds.length) {
        return;
      }

      this.sending = true;
      var body = {
        user_ids: pending.userIds.slice(),
        type: this.toApiChatType("personal") || "direct",
        state: "open",
        priority: "normal",
        message: messageText,
      };

      this.$axios
        .post(this.$endpoint + "chat", body)
        .then(function (response) {
          var resBody = response && response.data ? response.data : {};
          var data = resBody.data || {};
          var chat = data.chat || data || null;
          if (!chat) {
            return;
          }
          if (!chat.type) {
            chat.type = body.type;
          }
          var mapped = self.mapChatToConversation(chat, "personal", 2);
          if (pending.title) {
            mapped.title = pending.title;
          }
          if (pending.peerUserId != null) {
            mapped.peerUserId = pending.peerUserId;
          } else if (pending.userIds && pending.userIds[0] != null) {
            mapped.peerUserId = pending.userIds[0];
          }
          var ownerId =
            chat.user_id != null
              ? chat.user_id
              : chat.user && chat.user.id != null
                ? chat.user.id
                : data.first_message && data.first_message.user_id != null
                  ? data.first_message.user_id
                  : null;
          if (ownerId != null) {
            mapped.userId = ownerId;
            self.rememberMyChatUserId(ownerId);
          }
          mapped.preview = previewText || messageText;
          mapped.time = self.nowLabel();

          var pendingId = pending.id;
          self.upsertMappedConversation(mapped);
          self.pendingConversation = null;
          self.selectedId = mapped.id;
          if (pendingId != null && self.messagesMap[pendingId]) {
            self.$delete(self.messagesMap, pendingId);
          }

          var firstMessage = data.first_message || null;
          var messageId =
            firstMessage && firstMessage.id != null ? firstMessage.id : null;

          if (files.length && messageId != null) {
            return self
              .uploadChatMessageFiles(messageId, files)
              .then(function () {
                return self.loadMessages(mapped.id, true);
              })
              .then(function () {
                return self.loadMembers(mapped.id, true);
              });
          }

          if (firstMessage) {
            self.$set(self.messagesMap, mapped.id, [
              self.mapMessage(firstMessage, mapped.id),
            ]);
            return self.loadMembers(mapped.id, true);
          }

          // Fallback: chat created but first_message missing → send message separately
          return self.$axios
            .post(self.$endpoint + "chatMessage", {
              chat_id: mapped.id,
              message: messageText,
            })
            .then(function (msgRes) {
              var msgBody = msgRes && msgRes.data ? msgRes.data : {};
              var row = msgBody.data || null;
              var mid = row && row.id != null ? row.id : null;
              if (files.length && mid != null) {
                return self
                  .uploadChatMessageFiles(mid, files)
                  .then(function () {
                    return self.loadMessages(mapped.id, true);
                  });
              }
              if (row) {
                self.$set(self.messagesMap, mapped.id, [
                  self.mapMessage(row, mapped.id),
                ]);
              }
              return self.loadMembers(mapped.id, true);
            });
        })
        .catch(function (error) {
          console.error("create personal chat on first message error:", error);
        })
        .finally(function () {
          self.sending = false;
        });
    },
    upsertConversation(chat, forcedType, memberCount) {
      var mapped = this.mapChatToConversation(chat, forcedType, memberCount);
      return this.upsertMappedConversation(mapped);
    },
    upsertMappedConversation(mapped) {
      var index = this.conversations.findIndex(function (item) {
        return Number(item.id) === Number(mapped.id);
      });
      if (index > -1) {
        this.$set(
          this.conversations,
          index,
          Object.assign({}, this.conversations[index], mapped)
        );
        if (mapped && mapped.id != null && mapped.type) {
          this.setChatUnreadMeta(
            mapped.id,
            mapped.unread,
            mapped.type
          );
        }
        return this.conversations[index];
      }
      this.conversations.unshift(mapped);
      if (mapped && mapped.id != null && mapped.type) {
        this.setChatUnreadMeta(mapped.id, mapped.unread, mapped.type);
      }
      return mapped;
    },
    findByRef(refType, refId, list) {
      var type = String(refType || "");
      var id = Number(refId);
      var items = list || [];
      for (var i = 0; i < items.length; i++) {
        if (
          String(items[i].refType || "") === type &&
          Number(items[i].refId) === id
        ) {
          return items[i];
        }
      }
      return null;
    },
    syncFocusedTitle() {
      var refType = this.resolvedRefType;
      var refId = this.resolvedRefId;
      var title = this.resolvedTitle;
      if (!refType || refId == null || !title) {
        return;
      }
      if (
        this.pendingConversation &&
        String(this.pendingConversation.refType) === String(refType) &&
        Number(this.pendingConversation.refId) === Number(refId)
      ) {
        this.pendingConversation.title = title;
      }
      var matched = this.findByRef(refType, refId, this.conversations);
      if (matched) {
        matched.title = title;
      }
    },
    mapChatToConversation(chat, forcedType, memberCount) {
      var refType = chat.ref_type || null;
      var refId = chat.ref_id != null ? chat.ref_id : null;
      var type = this.resolveChatType(chat, memberCount, forcedType);

      var last = chat.last_message || null;
      var preview = last && last.message ? last.message : "";
      var time = "";
      if (last && last.time) {
        time = last.time;
      } else if (chat.date) {
        time = chat.date;
      }

      var accessUsers =
        chat.access_users ||
        chat.chat_user_access ||
        chat.chatUserAccess ||
        chat.users ||
        [];
      var title = "";
      var peerUserId = null;
      var avatarUrl = "";
      if (type === "ticket") {
        if (
          this.resolvedRefType === "ticket" &&
          Number(this.resolvedRefId) === Number(refId) &&
          this.resolvedTitle
        ) {
          title = this.resolvedTitle;
        } else {
          title = preview || "";
        }
      } else if (type === "personal") {
        title = this.resolvePersonalTitle(chat, accessUsers);
        peerUserId = this.resolvePersonalPeerId(chat, accessUsers);
        avatarUrl = this.resolveUserAvatarUrl(
          this.resolvePersonalPeerUser(chat, accessUsers)
        );
      } else if (type === "group" || type === "channel") {
        var rememberedTitle = this.getRememberedGroup(chat.id);
        title =
          (chat.name && String(chat.name).trim()) ||
          (rememberedTitle && rememberedTitle.title) ||
          this.resolveGroupTitle(chat, accessUsers);
      } else if (chat.user) {
        title = this.formatUserName(chat.user);
      }
      if (!title) {
        if (type !== "ticket") {
          title = preview || "#" + chat.id;
        }
      } else if (type === "ticket" && refId != null) {
        title = this.normalizeTicketTitle(title, refId);
      }

      return {
        id: chat.id,
        type: type,
        refType: refType,
        refId: refId,
        ticketId: refType === "ticket" ? Number(refId) : null,
        peerUserId: peerUserId,
        title: title,
        preview: preview,
        time: time,
        unread: Number(chat.unread_count) || 0,
        avatarUrl: avatarUrl,
        avatarColor: this.avatarColorForId(
          peerUserId != null ? peerUserId : chat.id
        ),
        state: chat.state || "open",
        priority: chat.priority || "normal",
        user: chat.user || null,
        userId: chat.user_id != null ? chat.user_id : chat.user && chat.user.id,
        members: Array.isArray(accessUsers)
          ? accessUsers.length
          : memberCount || 0,
      };
    },
    resolvePersonalPeerUser(chat, accessUsers) {
      var list = accessUsers || [];
      for (var i = 0; i < list.length; i++) {
        var row = list[i];
        if (this.isAccessCurrentUser(row)) {
          continue;
        }
        var uid = this.resolveMemberUserId(row);
        if (uid != null && !this.isCurrentUserIdentity(uid)) {
          return this.resolveMemberUser(row) || row;
        }
      }
      if (
        chat &&
        chat.user &&
        chat.user.id != null &&
        !this.isCurrentUserIdentity(chat.user.id) &&
        !this.isCurrentUserIdentity(chat.user_id)
      ) {
        return chat.user;
      }
      return null;
    },
    resolvePersonalPeerId(chat, accessUsers) {
      var list = accessUsers || [];
      for (var i = 0; i < list.length; i++) {
        var row = list[i];
        if (this.isAccessCurrentUser(row)) {
          continue;
        }
        var uid = this.resolveMemberUserId(row);
        if (uid != null && !this.isCurrentUserIdentity(uid)) {
          return uid;
        }
      }
      if (
        chat &&
        chat.user &&
        chat.user.id != null &&
        !this.isCurrentUserIdentity(chat.user.id) &&
        !this.isCurrentUserIdentity(chat.user_id)
      ) {
        return chat.user.id;
      }
      return null;
    },
    resolvePersonalTitle(chat, accessUsers) {
      var currentId = this.currentUserId;
      var list = accessUsers || [];
      for (var i = 0; i < list.length; i++) {
        var row = list[i];
        var uid = this.resolveMemberUserId(row);
        if (
          this.isAccessCurrentUser(row) ||
          (currentId != null && Number(uid) === Number(currentId))
        ) {
          continue;
        }
        var name = this.formatUserName(this.resolveMemberUser(row) || row);
        if (name) {
          return name;
        }
      }
      if (chat.user && Number(chat.user_id) !== Number(currentId)) {
        return this.formatUserName(chat.user);
      }
      return "";
    },
    resolveGroupTitle(chat, accessUsers) {
      if (chat && chat.name) {
        var named = String(chat.name).trim();
        if (named) {
          return named;
        }
      }
      var list = accessUsers || [];
      var names = [];
      for (var i = 0; i < list.length && names.length < 3; i++) {
        var name = this.formatUserName(
          this.resolveMemberUser(list[i]) || list[i]
        );
        if (name) {
          names.push(name);
        }
      }
      if (names.length) {
        return list.length > 3 ? names.join("، ") + "…" : names.join("، ");
      }
      if (chat.user) {
        return this.formatUserName(chat.user);
      }
      return "";
    },
    mapMessage(row, chatId) {
      var user = row.user || null;
      var userId = row.user_id != null ? row.user_id : user && user.id;
      var mine = this.isMessageMine(row);
      if (mine && userId != null) {
        this.rememberMyChatUserId(userId);
      }

      var replyTo = row.reply_to || null;
      var forwardedFrom = row.forwarded_from || null;
      var mentions = Array.isArray(row.mentions) ? row.mentions : [];
      var senderName = mine
        ? this.$t("conversation-me")
        : this.resolveSenderDisplayName(user, userId, chatId);

      return {
        id: row.id,
        text: row.message || "",
        mine: mine,
        sender: senderName,
        time: row.time || "",
        userId: userId,
        avatarUrl: this.resolveUserAvatarUrl(user),
        avatarColor:
          (user && user.color) || this.avatarColorForId(userId),
        initials: this.initialsFromName(mine ? "" : senderName),
        isReadByMe: !!row.is_read_by_me,
        isEdited: !!(row.is_edited || row.isEdited),
        readers: this.resolveMessageReaders(row, chatId),
        readersCount: this.resolveMessageReadersCount(row),
        attachments: this.mapMessageAttachments(row),
        replyTo: replyTo
          ? {
              id: replyTo.id,
              text: replyTo.message || "",
              sender:
                this.formatUserName(replyTo.user) ||
                this.resolveSenderDisplayName(
                  replyTo.user,
                  replyTo.user_id != null
                    ? replyTo.user_id
                    : replyTo.user && replyTo.user.id,
                  chatId
                ),
              deleted: !!replyTo.deleted,
            }
          : null,
        forwardedFrom: forwardedFrom
          ? {
              text: forwardedFrom.message || "",
              sender:
                this.formatUserName(forwardedFrom.user) ||
                this.resolveSenderDisplayName(
                  forwardedFrom.user,
                  forwardedFrom.user_id != null
                    ? forwardedFrom.user_id
                    : forwardedFrom.user && forwardedFrom.user.id,
                  chatId
                ),
              userId:
                forwardedFrom.user_id != null
                  ? forwardedFrom.user_id
                  : forwardedFrom.user && forwardedFrom.user.id,
            }
          : null,
        mentions: mentions.map(function (m) {
          return {
            userId: m.user_id != null ? m.user_id : m.user && m.user.id,
            name: m.user
              ? [m.user.firstname, m.user.lastname].filter(Boolean).join(" ")
              : "",
          };
        }),
      };
    },
    resolveSenderDisplayName(user, userId, chatId) {
      var name = this.formatUserName(user);
      if (name) {
        return String(name).trim();
      }
      if (userId == null) {
        return "";
      }
      var members =
        chatId != null && this.membersMap
          ? this.membersMap[chatId]
          : null;
      if (!Array.isArray(members) || !members.length) {
        members = this.activeMembersFallback(chatId);
      }
      if (Array.isArray(members)) {
        for (var i = 0; i < members.length; i++) {
          var row = members[i];
          var uid = this.resolveMemberUserId(row);
          if (uid != null && Number(uid) === Number(userId)) {
            var memberName = this.formatUserName(
              this.resolveMemberUser(row) || row
            );
            if (memberName) {
              return String(memberName).trim();
            }
          }
        }
      }
      return "";
    },
    activeMembersFallback(chatId) {
      if (chatId == null) {
        return [];
      }
      if (
        this.selectedId != null &&
        Number(this.selectedId) === Number(chatId) &&
        Array.isArray(this.membersMap[chatId])
      ) {
        return this.membersMap[chatId];
      }
      return this.membersMap[chatId] || [];
    },
    enrichMessagesSenders(chatId) {
      if (chatId == null || !this.messagesMap[chatId]) {
        return;
      }
      var self = this;
      var list = this.messagesMap[chatId];
      var changed = false;
      var next = list.map(function (msg) {
        if (!msg || msg.system || msg.mine) {
          return msg;
        }
        var name = String(msg.sender || "").trim();
        if (name && name !== "?") {
          return msg;
        }
        var resolved = self.resolveSenderDisplayName(
          null,
          msg.userId,
          chatId
        );
        if (!resolved) {
          return msg;
        }
        changed = true;
        return Object.assign({}, msg, {
          sender: resolved,
          initials: self.initialsFromName(resolved),
          avatarColor: msg.avatarColor || self.avatarColorForId(msg.userId),
        });
      });
      if (changed) {
        this.$set(this.messagesMap, chatId, next);
      }
    },
    isMessageMine(row) {
      if (!row) {
        return false;
      }
      var user = row.user || null;
      var userId = row.user_id != null ? row.user_id : user && user.id;
      if (this.isCurrentUserIdentity(userId)) {
        return true;
      }
      if (user) {
        if (this.isCurrentUserIdentity(user.id)) {
          return true;
        }
        if (user.uid != null && this.isSameUserId(user.uid, this.currentUserId)) {
          return true;
        }
        if (this.isCurrentUserIdentity(user.uid)) {
          return true;
        }
      }
      return false;
    },
    reclassifyMessagesMine(chatId) {
      if (chatId == null || !this.messagesMap[chatId]) {
        return;
      }
      var list = this.messagesMap[chatId];
      var changed = false;
      for (var i = 0; i < list.length; i++) {
        var msg = list[i];
        if (!msg || msg.system) {
          continue;
        }
        var nextMine =
          this.isCurrentUserIdentity(msg.userId) ||
          (msg.userId != null &&
            this.isSameUserId(msg.userId, this.resolveMyChatUserId()));
        // Keep already-correct mine; only flip when identity is now known
        if (nextMine && !msg.mine) {
          this.$set(msg, "mine", true);
          this.$set(msg, "sender", this.$t("conversation-me"));
          changed = true;
        }
      }
      if (changed) {
        this.$set(this.messagesMap, chatId, list.slice());
      }
    },
    formatUserName(user) {
      if (!user) {
        return "";
      }
      if (user.name) {
        return user.name;
      }
      var parts = [user.firstname, user.lastname].filter(Boolean);
      return parts.join(" ").trim();
    },
    normalizeTicketTitle(title, refId) {
      var value = String(title || "").trim();
      if (!value || refId == null) {
        return value;
      }
      var id = String(refId);
      var persian = id.replace(/\d/g, function (d) {
        return "۰۱۲۳۴۵۶۷۸۹"[d];
      });
      // title is only the ticket id → drop it (UI already shows #id)
      if (
        value === id ||
        value === persian ||
        value === "#" + id ||
        value === "#" + persian
      ) {
        return "";
      }
      // strip leading "#7253 " / "#۷۲۵۳ "
      var patterns = [
        new RegExp("^#" + id + "\\s*", "i"),
        new RegExp("^#" + persian + "\\s*"),
        new RegExp("^" + id + "\\s*"),
        new RegExp("^" + persian + "\\s*"),
      ];
      for (var i = 0; i < patterns.length; i++) {
        value = value.replace(patterns[i], "");
      }
      return value.trim();
    },
    extractList(response) {
      var body = response && response.data ? response.data : {};
      var data = body.data;
      if (Array.isArray(data)) {
        return data;
      }
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      return [];
    },
    extractMessageList(response) {
      var body = response && response.data ? response.data : {};
      if (Array.isArray(body.data)) {
        return body.data;
      }
      if (body.data && Array.isArray(body.data.data)) {
        return body.data.data;
      }
      if (Array.isArray(body)) {
        return body;
      }
      return [];
    },
    extractAccessList(response) {
      var body = response && response.data ? response.data : {};
      if (Array.isArray(body.data)) {
        return body.data;
      }
      if (body.data && Array.isArray(body.data.data)) {
        return body.data.data;
      }
      if (Array.isArray(body)) {
        return body;
      }
      return [];
    },
    nowLabel() {
      var d = new Date();
      var h = d.getHours();
      var m = d.getMinutes();
      var pad = function (n) {
        return n < 10 ? "0" + n : String(n);
      };
      return this.toPersianDigits(pad(h) + ":" + pad(m));
    },
    toPersianDigits(value) {
      return String(value).replace(/\d/g, function (d) {
        return "۰۱۲۳۴۵۶۷۸۹"[d];
      });
    },
    avatarColorForId(id) {
      var colors = [
        "#0066FF",
        "#00897B",
        "#7B61FF",
        "#FF6B35",
        "#E91E63",
        "#FF9800",
        "#4CAF50",
      ];
      var n = Number(id) || 0;
      if (!n) {
        return colors[0];
      }
      return colors[Math.abs(n) % colors.length];
    },
    resolveUserAvatarUrl(user) {
      if (!user) {
        return "";
      }
      var avatar = user.avatar;
      if (avatar && typeof avatar === "object") {
        return avatar.link || avatar.url || avatar.path || "";
      }
      if (typeof avatar === "string" && avatar) {
        return avatar;
      }
      var nested =
        user.avatar_url ||
        user.photo ||
        user.image ||
        user.profile_image ||
        null;
      if (nested && typeof nested === "object") {
        return nested.link || nested.url || nested.path || "";
      }
      return nested || "";
    },
    initialsFromName(name) {
      var value = String(name || "").trim();
      if (!value) {
        return "?";
      }
      var parts = value.split(/\s+/).filter(Boolean);
      if (parts.length === 1) {
        return parts[0].slice(0, 2);
      }
      return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
    },
    toApiChatType(uiType) {
      if (uiType === "personal" || uiType === "direct") {
        return "direct";
      }
      if (uiType === "channel") {
        return "channel";
      }
      if (uiType === "group") {
        return "group";
      }
      if (uiType === "ticket") {
        return "ticket";
      }
      return null;
    },
    fromApiChatType(apiType, refType) {
      if (refType === "ticket" || apiType === "ticket") {
        return "ticket";
      }
      if (apiType === "direct" || apiType === "personal") {
        return "personal";
      }
      if (apiType === "channel") {
        return "channel";
      }
      if (apiType === "group") {
        return "group";
      }
      return null;
    },
    resolveChatType(chat, memberCount, forcedType) {
      var refType = chat && chat.ref_type;
      if (refType === "ticket" || forcedType === "ticket") {
        return "ticket";
      }

      var apiRaw =
        chat &&
        (chat.type === "direct" ||
        chat.type === "group" ||
        chat.type === "channel" ||
        chat.type === "personal" ||
        chat.type === "ticket"
          ? chat.type
          : chat.chat_type || chat.chatType || null);

      var fromApi = this.fromApiChatType(apiRaw, refType);
      if (fromApi) {
        return fromApi;
      }

      if (
        forcedType === "personal" ||
        forcedType === "group" ||
        forcedType === "channel"
      ) {
        return forcedType;
      }

      if (refType) {
        return "group";
      }

      var count = memberCount;
      if (count == null && chat && Array.isArray(chat.access_users)) {
        count = chat.access_users.length;
      }
      if (count != null && Number(count) > 2) {
        return "group";
      }
      return "personal";
    },
    syncChatRoomSubscription(chatId) {
      var realtime =
        this.$root && this.$root.chatRealtime ? this.$root.chatRealtime : null;
      var prev = this.subscribedChatRoomId;
      var next =
        chatId != null && String(chatId).indexOf("pending-") !== 0
          ? chatId
          : null;
      if (realtime) {
        if (prev != null && Number(prev) !== Number(next)) {
          realtime.unsubscribeChatRoom(prev);
        }
        if (next != null) {
          realtime.subscribeChatRoom(next);
        }
      }
      this.subscribedChatRoomId = next;
    },
    onChatRealtimeReady() {
      var id = this.selectedId;
      this.subscribedChatRoomId = null;
      this.syncChatRoomSubscription(id);
    },
    onChatLiveEvent(payload) {
      var data = this.extractChatLiveData(payload);
      if (!this.isLiveEventForMe(data)) {
        return;
      }
      var action = normalizeChatEventName(payload);
      if (!action && data && data.action) {
        action = String(data.action).replace(/^\./, "");
      }
      action = String(action || "")
        .replace(/^\./, "")
        .toLowerCase();

      if (action === "chat.created") {
        this.handleLiveChatCreated(data);
        return;
      }
      if (action === "chat.updated") {
        this.handleLiveChatUpdated(data);
        return;
      }
      if (action === "chat.deleted") {
        this.handleLiveChatDeleted(data);
        return;
      }
      if (action === "chat.message.created") {
        this.handleLiveMessageCreated(data);
        return;
      }
      if (action === "chat.message.updated") {
        this.handleLiveMessageUpdated(data);
        return;
      }
      if (action === "chat.message.deleted") {
        this.handleLiveMessageDeleted(data);
        return;
      }
      if (action === "chat.message.read") {
        this.handleLiveMessageRead(data);
        return;
      }
      if (action === "chat.badge") {
        this.applyMyBadgeFromPayload(data);
      }
    },
    extractChatLiveData(payload) {
      var root = payload && payload.data != null ? payload.data : payload;
      if (typeof root === "string") {
        try {
          root = JSON.parse(root);
        } catch (e) {
          return {};
        }
      }
      if (!root || typeof root !== "object") {
        return {};
      }
      if (
        root.data &&
        typeof root.data === "object" &&
        !Array.isArray(root.data) &&
        (root.data.chat_id != null ||
          root.data.action ||
          root.data.message ||
          root.data.chat)
      ) {
        return Object.assign({}, root, root.data);
      }
      return root;
    },
    isLiveEventForMe(data) {
      if (!data) {
        return false;
      }
      // Try to learn numeric chat user id from badges / DM heuristics first
      this.findMyLiveBadge(data);

      var ids = data.member_ids;
      if (Array.isArray(ids) && ids.length) {
        for (var i = 0; i < ids.length; i++) {
          if (this.isCurrentUserIdentity(ids[i])) {
            return true;
          }
        }
        // Chat already in my sidebar → I have access; don't drop the event
        var chatId = this.liveChatId(data);
        if (chatId != null && this.findConversationById(chatId)) {
          return true;
        }
        return false;
      }
      return true;
    },
    liveChatId(data) {
      if (!data) {
        return null;
      }
      if (data.chat_id != null) {
        return data.chat_id;
      }
      if (data.chatId != null) {
        return data.chatId;
      }
      if (data.chat && data.chat.id != null) {
        return data.chat.id;
      }
      return null;
    },
    findConversationById(chatId) {
      if (chatId == null) {
        return null;
      }
      return (
        this.conversations.find(function (item) {
          return Number(item.id) === Number(chatId);
        }) || null
      );
    },
    applyMyBadgeFromPayload(data) {
      var chatId = this.liveChatId(data);
      if (chatId == null || !Array.isArray(data.badges)) {
        return;
      }
      var mine = this.findMyLiveBadge(data);
      if (!mine) {
        return;
      }
      var conversation = this.findConversationById(chatId);
      var type = this.resolveLiveChatUiType(data, conversation);
      var next = Number(mine.unread_count) || 0;
      if (conversation) {
        var prev = Number(conversation.unread) || 0;
        if (prev !== next) {
          this.$set(conversation, "unread", next);
          this.totalUnread = Math.max(
            0,
            (Number(this.totalUnread) || 0) - prev + next
          );
        }
        if (!type) {
          type = conversation.type;
        }
      }
      if (type) {
        this.setChatUnreadMeta(chatId, next, type);
      }
    },
    resolveLiveChatUiType(data, conversation) {
      if (conversation && conversation.type) {
        return conversation.type;
      }
      var forced = this.liveForcedType(data);
      if (forced) {
        return forced;
      }
      var raw = data && (data.list || data.type);
      return this.fromApiChatType(raw, data && data.ref_type);
    },
    bumpConversationPreview(chatId, messageRow) {
      var conversation = this.findConversationById(chatId);
      if (!conversation || !messageRow) {
        return;
      }
      var text =
        messageRow.message != null
          ? messageRow.message
          : messageRow.text != null
            ? messageRow.text
            : "";
      var time = messageRow.time || this.nowLabel();
      this.$set(conversation, "preview", text);
      this.$set(conversation, "time", time);
      this.bumpConversationToTop(chatId);
    },
    bumpConversationToTop(chatId) {
      var index = this.conversations.findIndex(function (item) {
        return Number(item.id) === Number(chatId);
      });
      if (index <= 0) {
        return;
      }
      var item = this.conversations.splice(index, 1)[0];
      this.conversations.unshift(item);
    },
    resolveMessageReaders(row, chatId) {
      if (!row || !Array.isArray(row.readers)) {
        return [];
      }
      var senderId =
        row.user_id != null ? row.user_id : row.user && row.user.id;
      var list = [];
      var seen = {};
      for (var i = 0; i < row.readers.length; i++) {
        var reader = row.readers[i];
        if (!reader) {
          continue;
        }
        var readerUser = reader.user || null;
        var readerId =
          reader.user_id != null
            ? reader.user_id
            : readerUser && readerUser.id;
        if (readerId == null) {
          continue;
        }
        if (senderId != null && this.isSameUserId(readerId, senderId)) {
          continue;
        }
        var key = String(readerId);
        if (seen[key]) {
          continue;
        }
        seen[key] = true;
        var name =
          this.formatUserName(readerUser) ||
          this.resolveSenderDisplayName(readerUser, readerId, chatId) ||
          "#" + readerId;
        name = String(name).trim();
        list.push({
          userId: readerId,
          name: name,
          avatarUrl: this.resolveUserAvatarUrl(readerUser),
          avatarColor:
            (readerUser && readerUser.color) ||
            this.avatarColorForId(readerId),
          initials: this.initialsFromName(name),
        });
      }
      return list;
    },
    resolveMessageReadersCount(row) {
      if (!row) {
        return 0;
      }
      if (Array.isArray(row.readers)) {
        return this.resolveMessageReaders(row, null).length;
      }
      var senderId =
        row.user_id != null ? row.user_id : row.user && row.user.id;
      var raw = Number(row.readers_count);
      if (isNaN(raw) || raw <= 0) {
        return 0;
      }
      // Many APIs set readers_count=1 for the sender's own read receipt
      if (
        raw === 1 &&
        senderId != null &&
        this.isCurrentUserIdentity(senderId)
      ) {
        return 0;
      }
      return raw;
    },
    upsertLiveMessage(chatId, messageRow) {
      if (chatId == null || !messageRow || messageRow.id == null) {
        return;
      }
      if (!this.messagesMap[chatId]) {
        if (Number(this.selectedId) !== Number(chatId)) {
          return;
        }
        this.$set(this.messagesMap, chatId, []);
      }
      var mapped = this.mapMessage(messageRow, chatId);
      var list = this.messagesMap[chatId].slice();
      var index = list.findIndex(function (item) {
        return Number(item.id) === Number(mapped.id);
      });
      if (index > -1) {
        var prev = list[index];
        var next = Object.assign({}, prev, mapped, {
          pending: false,
          failed: false,
        });
        var incomingHasReadersMeta =
          Array.isArray(messageRow.readers) ||
          (messageRow.readers_count != null &&
            messageRow.readers_count !== "");
        // Incomplete live payloads must not wipe an already-known seen state
        if (!incomingHasReadersMeta) {
          next.readersCount = Number(prev.readersCount) || 0;
          next.readers = Array.isArray(prev.readers) ? prev.readers : [];
        } else if (
          (!mapped.readers || !mapped.readers.length) &&
          Array.isArray(prev.readers) &&
          prev.readers.length
        ) {
          next.readers = prev.readers;
          next.readersCount = Math.max(
            Number(mapped.readersCount) || 0,
            prev.readers.length
          );
        }
        this.$set(list, index, next);
      } else if (mapped.mine) {
        var pendingIdx = list.findIndex(function (item) {
          return (
            item &&
            item.pending &&
            item.mine &&
            String(item.text || "") === String(mapped.text || "")
          );
        });
        if (pendingIdx > -1) {
          this.$set(
            list,
            pendingIdx,
            Object.assign({}, list[pendingIdx], mapped, {
              pending: false,
              failed: false,
            })
          );
        } else {
          list.push(mapped);
        }
      } else {
        list.push(mapped);
      }
      this.$set(this.messagesMap, chatId, list);
    },
    removeLiveMessage(chatId, messageId) {
      if (chatId == null || messageId == null || !this.messagesMap[chatId]) {
        return;
      }
      var list = this.messagesMap[chatId].filter(function (item) {
        return Number(item.id) !== Number(messageId);
      });
      this.$set(this.messagesMap, chatId, list);
    },
    fetchAndUpsertChat(chatId, forcedType) {
      var self = this;
      if (chatId == null) {
        return Promise.resolve(null);
      }
      return this.$axios
        .get(this.$endpoint + "chat/" + chatId)
        .then(function (response) {
          var chat = response && response.data ? response.data : null;
          if (chat && chat.data) {
            chat = chat.data;
          }
          if (!chat) {
            return null;
          }
          return self.upsertConversation(chat, forcedType);
        })
        .catch(function (error) {
          console.error("live fetch chat error:", error);
          return null;
        });
    },
    liveForcedType(data) {
      var raw = (data && (data.list || data.type)) || null;
      return this.fromApiChatType(raw, data && data.ref_type);
    },
    handleLiveChatCreated(data) {
      var self = this;
      var chatId = this.liveChatId(data);
      var forcedType = this.liveForcedType(data);
      var chat = data.chat || null;
      if (chat) {
        this.upsertConversation(chat, forcedType);
        this.applyMyBadgeFromPayload(data);
        return;
      }
      if (chatId == null) {
        return;
      }
      if (this.findConversationById(chatId)) {
        this.applyMyBadgeFromPayload(data);
        return;
      }
      this.fetchAndUpsertChat(chatId, forcedType).then(function () {
        self.applyMyBadgeFromPayload(data);
      });
    },
    handleLiveChatUpdated(data) {
      var self = this;
      var chatId = this.liveChatId(data);
      var forcedType = this.liveForcedType(data);
      var chat = data.chat || null;
      if (chat) {
        var existing = this.findConversationById(chat.id || chatId);
        var mapped = this.mapChatToConversation(chat, forcedType);
        if (existing && (chat.unread_count == null || chat.unread_count === "")) {
          mapped.unread = existing.unread;
        }
        this.upsertMappedConversation(mapped);
        this.applyMyBadgeFromPayload(data);
        if (
          Number(this.selectedId) === Number(mapped.id) &&
          data.members_changed
        ) {
          this.loadMembers(mapped.id, true);
        }
        return;
      }
      if (chatId == null) {
        return;
      }
      var conversation = this.findConversationById(chatId);
      if (conversation) {
        if (data.state) {
          this.$set(conversation, "state", data.state);
        }
        if (data.priority) {
          this.$set(conversation, "priority", data.priority);
        }
        if (forcedType) {
          this.$set(conversation, "type", forcedType);
        }
        this.applyMyBadgeFromPayload(data);
        return;
      }
      this.fetchAndUpsertChat(chatId, forcedType).then(function () {
        self.applyMyBadgeFromPayload(data);
      });
    },
    handleLiveChatDeleted(data) {
      var chatId = this.liveChatId(data);
      if (chatId == null) {
        return;
      }
      var conversation = this.findConversationById(chatId);
      if (conversation) {
        var unread = Number(conversation.unread) || 0;
        if (unread > 0) {
          this.totalUnread = Math.max(
            0,
            (Number(this.totalUnread) || 0) - unread
          );
        }
      }
      this.clearChatUnreadMeta(chatId);
      this.conversations = this.conversations.filter(function (item) {
        return Number(item.id) !== Number(chatId);
      });
      if (this.messagesMap[chatId]) {
        this.$delete(this.messagesMap, chatId);
      }
      if (this.membersMap[chatId]) {
        this.$delete(this.membersMap, chatId);
      }
      if (Number(this.selectedId) === Number(chatId)) {
        this.selectedId = null;
        this.membersPanelOpen = false;
      }
      if (Number(this.subscribedChatRoomId) === Number(chatId)) {
        this.syncChatRoomSubscription(null);
      }
    },
    handleLiveMessageCreated(data) {
      var chatId = this.liveChatId(data);
      var message = data.message || null;
      if (chatId == null) {
        return;
      }
      if (!this.findConversationById(chatId)) {
        var self = this;
        this.fetchAndUpsertChat(chatId, this.liveForcedType(data)).then(
          function () {
            if (message) {
              self.bumpConversationPreview(chatId, message);
              if (Number(self.selectedId) === Number(chatId)) {
                self.upsertLiveMessage(chatId, message);
              }
            }
            self.applyMyBadgeFromPayload(data);
          }
        );
        return;
      }
      if (message) {
        this.bumpConversationPreview(chatId, message);
        if (Number(this.selectedId) === Number(chatId)) {
          this.upsertLiveMessage(chatId, message);
          if (!this.isPendingSelection) {
            this.markChatAsRead(chatId);
          }
        }
      }
      this.applyMyBadgeFromPayload(data);
    },
    handleLiveMessageUpdated(data) {
      var chatId = this.liveChatId(data);
      var message = data.message || null;
      if (chatId == null || !message) {
        return;
      }
      if (Number(this.selectedId) === Number(chatId)) {
        this.upsertLiveMessage(chatId, message);
      }
      var list = this.messagesMap[chatId];
      if (list && list.length) {
        var last = list[list.length - 1];
        if (last && Number(last.id) === Number(message.id)) {
          this.bumpConversationPreview(chatId, message);
        }
      }
    },
    handleLiveMessageDeleted(data) {
      var chatId = this.liveChatId(data);
      var messageId =
        data.message_id != null
          ? data.message_id
          : data.message && data.message.id;
      if (chatId == null || messageId == null) {
        return;
      }
      this.removeLiveMessage(chatId, messageId);
      this.applyMyBadgeFromPayload(data);
    },
    handleLiveMessageRead(data) {
      var self = this;
      var chatId = this.liveChatId(data);
      var readerUser = data.reader || data.user || null;
      var readerId =
        data.reader_user_id != null
          ? data.reader_user_id
          : data.user_id != null
            ? data.user_id
            : readerUser && readerUser.id;
      if (chatId == null) {
        this.applyMyBadgeFromPayload(data);
        return;
      }
      if (
        readerId != null &&
        !this.isCurrentUserIdentity(readerId) &&
        Number(this.selectedId) === Number(chatId) &&
        this.messagesMap[chatId]
      ) {
        var readerName =
          this.formatUserName(readerUser) ||
          this.resolveSenderDisplayName(readerUser, readerId, chatId) ||
          "#" + readerId;
        var lastReadId =
          data.last_read_message_id != null
            ? Number(data.last_read_message_id)
            : null;
        var list = this.messagesMap[chatId].map(function (item) {
          if (!item.mine) {
            return item;
          }
          if (lastReadId != null && Number(item.id) > lastReadId) {
            return item;
          }
          var readers = Array.isArray(item.readers) ? item.readers.slice() : [];
          var already = readers.some(function (r) {
            return self.isSameUserId(r && r.userId, readerId);
          });
          if (!already) {
            readers.push({
              userId: readerId,
              name: String(readerName).trim(),
              avatarUrl: self.resolveUserAvatarUrl(readerUser),
              avatarColor:
                (readerUser && readerUser.color) ||
                self.avatarColorForId(readerId),
              initials: self.initialsFromName(readerName),
            });
          }
          return Object.assign({}, item, {
            readers: readers,
            readersCount: readers.length,
          });
        });
        this.$set(this.messagesMap, chatId, list);
      }
      this.applyMyBadgeFromPayload(data);
    },
  },
};
</script>

<style scoped>
.conversation-workspace {
  display: flex;
  flex-direction: row;
  height: 100%;
  min-height: 0;
  width: 100%;
  direction: ltr;
  background: #fff;
  position: relative;
}
.conversation-mentions-overlay {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.28);
  z-index: 40;
  display: flex;
  justify-content: flex-end;
}
.conversation-mentions-panel {
  width: 340px;
  max-width: 92%;
  height: 100%;
  background: #fff;
  box-shadow: -8px 0 28px rgba(15, 23, 42, 0.1);
  display: flex;
  flex-direction: column;
  direction: rtl;
}
.conversation-mentions-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid #eef0f3;
  flex: 0 0 auto;
}
.conversation-mentions-header-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}
.conversation-mentions-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #eef4ff;
  color: #0066ff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}
.conversation-mentions-close {
  border: 0;
  background: transparent;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}
.conversation-mentions-close:hover {
  background: #f3f4f6;
}
.conversation-mentions-body {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}
.conversation-mentions-body::-webkit-scrollbar {
  width: 4px;
}
.conversation-mentions-body::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.14);
  border-radius: 999px;
}
.conversation-mentions-loading,
.conversation-mentions-empty {
  padding: 40px 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}
.conversation-mentions-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 0;
  border-bottom: 1px solid #f3f4f6;
  background: transparent;
  text-align: right;
  cursor: pointer;
  transition: background 0.15s ease;
}
.conversation-mentions-item:hover {
  background: #f5f8fc;
}
.conversation-mentions-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex: 0 0 auto;
}
.conversation-mentions-meta {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.conversation-mentions-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}
.conversation-mentions-item-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.conversation-mentions-item-time {
  font-size: 11px;
  color: #9ca3af;
  flex: 0 0 auto;
  white-space: nowrap;
}
.conversation-mentions-item-text {
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;
}
.conversation-mentions-highlight {
  color: #0066ff;
  font-weight: 500;
}
</style>
